import { uploadBasketReceipt, ReceiptAttachmentError } from './services/receiptAttachments';
import { basketContextForRoute, isLatticeScanRoute, isRestrictedWorkspaceRoute } from './src/lib/navigationContext';
import { profileSummaryForAssistant, profileSignature, mergePersonalization, PROFILE_QUESTION_RULE, PERSONAL_EVIDENCE_RULE } from './src/lib/personalization';
import React, { useState, useMemo, useCallback, useRef, useEffect, useLayoutEffect, lazy, Suspense } from 'react';
import { cn } from './src/lib/utils';
import { SupabaseContext } from './src/lib/SupabaseContext';
import { Routes, Route, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useUser, useClerk, ClerkFailed } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { 
  Loader2, 
  Clock, 
  Zap, 
  X,
  MapPin,
  Lock,
  Lightbulb,
  ShieldCheck,
  ArrowRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import { useRegisterSW } from 'virtual:pwa-register/react';

// Component Imports
import SEO from './components/common/SEO';
import CinematicLoader from './components/CinematicLoader';
import { LandingPage } from './components/LandingPage';
import { AuthModal } from './components/AuthModal';
import { PrivacyBanner } from './components/common/PrivacyBanner';
import StagingRedirectModal from './components/StagingRedirectModal';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { PrivateRoute } from './components/common/PrivateRoute';
import { SessionLostNotice, useSupabaseSessionLost } from './components/common/SessionLostNotice';
import { ShoppingList } from './components/ShoppingList';
import { MaintenanceModal } from './components/MaintenanceModal';
import { PWAInstallModal } from './components/PWAInstallModal';
import { PWAUpdateModal } from './components/PWAUpdateModal';

// Lazy-loaded Views
const DashboardView = lazy(() => import('./components/DashboardView').then(m => ({ default: m.DashboardView })));
const HistoryView = lazy(() => import('./components/HistoryView').then(m => ({ default: m.HistoryView })));
const DiscoveryView = lazy(() => import('./components/DiscoveryView'));
const CategoryManager = lazy(() => import('./components/CategoryManager').then(m => ({ default: m.CategoryManager })));
const SettingsView = lazy(() => import('./components/SettingsView').then(m => ({ default: m.SettingsView })));
const AccountView = lazy(() => import('./components/AccountView').then(m => ({ default: m.AccountView })));
const ComparisonView = lazy(() => import('./components/ComparisonView').then(m => ({ default: m.ComparisonView })));
const PitchDeck = lazy(() => import('./components/PitchDeck'));
const DemosGalleryView = lazy(() => import('./components/DemosGalleryView'));
const DEMOS_PATH = '/demos';
const ProductTourView = lazy(() => import('./components/ProductTourView'));
// The product tour video as a page, recorded by scripts/render-tour.mjs. Unlisted: dev, or admins.
const TOUR_PATH = '/render/tour';
const AisleTourView = lazy(() => import('./components/AisleTourView'));
// The aisle video (Trip Budget while shopping), recorded the same way. Unlisted: dev, or admins.
const AISLE_PATH = '/render/aisle';
const LegalView = lazy(() => import('./components/LegalView'));
const ArchitectEnrollmentView = lazy(() => import('./components/ArchitectEnrollmentView'));
const AboutView = lazy(() => import('./components/AboutView'));
const PartnerEnrollmentView = lazy(() => import('./components/PartnerEnrollmentView'));
const EnrollmentCenter = lazy(() => import('./components/EnrollmentCenter'));
const AdminTerminal = lazy(() => import('./components/AdminTerminal'));
const AdminMetrics = lazy(() => import('./components/AdminMetrics'));

const ScoutDashboard = lazy(() => import('./components/ScoutDashboard'));
const MerchantDashboard = lazy(() => import('./components/MerchantDashboard'));
const BlogCMS = lazy(() => import('./components/BlogCMS').then(m => ({ default: m.BlogCMS })));
const BlogView = lazy(() => import('./components/BlogView'));
const BlogPostView = lazy(() => import('./components/BlogPostView'));
const AlternativesView = lazy(() => import('./components/AlternativesView').then(m => ({ default: m.AlternativesView })));
const AlternativeView = lazy(() => import('./components/AlternativeView').then(m => ({ default: m.AlternativeView })));
const BrandView = lazy(() => import('./components/BrandView').then(m => ({ default: m.BrandView })));
const CommoditiesView = lazy(() => import('./components/CommoditiesView'));
const UnitsView = lazy(() => import('./components/UnitsView'));
const FAQView = lazy(() => import('./components/FAQView'));
const FeaturesHubView = lazy(() => import('./components/FeaturesHubView'));
const PricingView = lazy(() => import('./components/PricingView'));
const FeaturePageView = lazy(() => import('./components/FeaturePageView'));
const NewsroomView = lazy(() => import('./components/NewsroomView'));
const NewsroomPostView = lazy(() => import('./components/NewsroomPostView'));
const SignInView = lazy(() => import('./components/SignInView'));
const SignUpView = lazy(() => import('./components/SignUpView'));

// Lazy-loaded: authenticated app shell only (never rendered on public routes) —
// keeps the CartAssistant/recharts/basket-modal weight out of the public bundle.
const AppHeader = lazy(() => import('./components/AppHeader').then(m => ({ default: m.AppHeader })));
const NavigationSidebar = lazy(() => import('./components/NavigationSidebar').then(m => ({ default: m.NavigationSidebar })));
const NotificationSystem = lazy(() => import('./components/NotificationSystem').then(m => ({ default: m.NotificationSystem })));
const AppBottomPanel = lazy(() => import('./components/AppBottomPanel').then(m => ({ default: m.AppBottomPanel })));
const StorePickerModal = lazy(() => import('./components/StorePickerModal').then(m => ({ default: m.StorePickerModal })));
const TripBudgetModal = lazy(() => import('./components/TripBudgetModal').then(m => ({ default: m.TripBudgetModal })));
const AddProductModal = lazy(() => import('./components/AddProductModal').then(m => ({ default: m.AddProductModal })));
const DateTimePickerModal = lazy(() => import('./components/DateTimePickerModal').then(m => ({ default: m.DateTimePickerModal })));
const CameraView = lazy(() => import('./components/CameraView').then(m => ({ default: m.CameraView })));
const BulkImportView = lazy(() => import('./components/BulkImportView').then(m => ({ default: m.BulkImportView })));
const BasketInsightsModal = lazy(() => import('./components/BasketInsightsModal').then(m => ({ default: m.BasketInsightsModal })));
const DigitalReceiptModal = lazy(() => import('./components/DigitalReceiptModal').then(m => ({ default: m.DigitalReceiptModal })));
const ImagePreviewModal = lazy(() => import('./components/ImagePreviewModal').then(m => ({ default: m.ImagePreviewModal })));
const SovereignTourController = lazy(() => import('./components/SovereignTourController').then(m => ({ default: m.SovereignTourController })));
const ConfirmationModal = lazy(() => import('./components/common/ConfirmationModal').then(m => ({ default: m.ConfirmationModal })));
const ArchiveTripModal = lazy(() => import('./components/ArchiveTripModal').then(m => ({ default: m.ArchiveTripModal })));
const LatticeSeedDetailModal = lazy(() => import('./components/LatticeSeedDetailModal').then(m => ({ default: m.LatticeSeedDetailModal })));
const UpgradeToProMessage = lazy(() => import('./components/common/UpgradeToProMessage').then(m => ({ default: m.UpgradeToProMessage })));
const FeatureGatedRoute = lazy(() => import('./components/common/FeatureGatedRoute').then(m => ({ default: m.FeatureGatedRoute })));
const OnboardingView = lazy(() => import('./components/OnboardingView').then(m => ({ default: m.OnboardingView })));

// CartAssistant SDK & Components
import { CartAssistantProvider, useCartAssistant } from "./src/lib/cartAssistant/CartAssistantProvider";
import { MessageThreadCollapsible } from "./src/components/message-thread-collapsible";
import { components, createTools } from "./src/lib/cartAssistant/registry";

// Service Imports
import { getSupabaseClient, setSupabaseAuthUser, clearSupabaseAuthUser, getExpectedSupabaseAuth, markSupabaseSessionLost } from './services/supabaseClient';
import { BlogFirstScanGuide } from './components/BlogFirstScanGuide';
import { recordBlogStage } from './services/blogConversion';
import { recordSignup, recordUsefulVerdict } from './services/acquisitionAnalytics';
import { trackMeta } from './services/metaAnalytics';
import { applyConsent, effectiveConsent, fetchConsentPolicy, loadCookieless } from './src/lib/consent';
import { enableSentryReplay } from './services/sentryService';
import { 
  getProfile, 
  getHistory, 
  getCategories, 
  setCategories as syncCategories,
  updateProfile, 
  getRestockSignals,
  getMasterInsight, 
  deleteHistoryEntry, 
  saveHistoryEntry,
  uploadItemImage,
  getLatticeSeedItems,
  getArchiveAllowance,
  type ArchiveAllowance
} from './services/supabaseService';
import { LatticeSeedItem, LatticeSeedScope } from './src/types/latticeSeed';
import { 
  getNearbyStores,
  searchStores,
  extractItemsFromImage,
  validateChatMessage,
  generateBasketInsight,
  autocompleteAddress,
  geocodeStore,
  classifyExtractionError,
  getGeminiErrorKey,
  reverseGeocodeCoords
} from './services/geminiService';
import { resolveMarketDeals, haversineMiles } from './src/lib/marketDiscovery';
import { geocodeAddress as geocodePlace } from './src/lib/geocoding';
import { 
  getProductSignatureRuleProtocol, 
  getProductSignatureRuleSummary,
  getLanguageName 
} from './services/promptConstants';

import { getRegionalConfig } from './services/locationService';
import { LanguageDetectionModal } from './components/LanguageDetectionModal';

// Store & Utils
import { useStore, liveState, buildCloudSyncPayload, profileZipCode } from './src/lib/store';
import { reconcileEntitlement, watchEntitlement } from './src/lib/entitlement';
import { BUDGET_TONE, BudgetStatus, getBudgetState } from './src/lib/tripBudget';
import { applyReceiptDateTime, parseReceiptDate } from './src/lib/receiptTrip';
import { resolveReceiptLocation } from './src/lib/receiptLocation';
import { resolveReceiptStore, geocodeReceiptStore } from './src/lib/receiptStore';
import { computeTripTotals } from './src/lib/basketItem';
import { useUIStore } from './src/lib/ui-store';
import { useMerchant } from './src/lib/hooks/useMerchant';
import { useSubscription } from './src/lib/hooks/useSubscription';
import { computeRestockItems, selectBasketSuggestions, restockSettingKeys, SNOOZE_DAYS, RestockItem, RestockSettings } from './src/lib/restock';
import { useQuery } from '@tanstack/react-query';
import { findStorePriceChange, type StorePriceChange } from './src/lib/priceChange';
import { queryKeys } from './src/lib/queryClient';
import { migrateLegacyConfirmations } from './src/lib/confirmations';
import { CURRENCIES, PUBLIC_PATHS } from './constants';
import { dataURLToBlob } from './services/imageUtils';
import { formatDisplayName, constructProtocolSlug } from './src/lib/intelligence';
import { useBulkImportStatusIndicator } from './src/lib/bulkImport';
import { 
  ShoppingItem, 
  CategoryInfo, 
  UserProfile, 
  StoreInfo, 
  ShoppingHistoryEntry, 
  InsightData,
  BasketTip
} from './types';
import L from 'leaflet';
import { resolveCalibration } from './src/lib/calibration';

// === Inline System Prompt Engine ===

interface PromptConfig {
  persona: string;
  protocol: string[];
  rules?: string[];
}

function buildSystemPrompt(config: PromptConfig): string {
  const parts: string[] = [];
  parts.push(`You are a ${config.persona}.`);
  parts.push('\n// **YOUR TASK**');
  parts.push(...config.protocol);
  if (config.rules) {
    parts.push('\n**RULES:**');
    config.rules.forEach(rule => parts.push(`- ${rule}`));
  }
  return parts.join('\n');
}

// === End Inlined Prompt Logic ===

type ScanMode = 'tag' | 'receipt' | 'import';

const CartAssistantBridge: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentThreadId } = useCartAssistant();

  // Keep liveState.lastThreadImageRef in sync with the current thread's last uploaded image
  useEffect(() => {
    if (!currentThreadId) return;
    import('./services/imageCacheService').then(({ ImageCacheService }) => {
      ImageCacheService.getThreadImageRef(currentThreadId).then(ref => {
        liveState.lastThreadImageRef = ref ? { ...ref, threadId: currentThreadId } : null;
      });
    });
  }, [currentThreadId]);

  return <>{children}</>;
};

// Global Error Boundary
class GlobalErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Global Error Boundary caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[100dvh] bg-[#0A0F1E] text-white p-6 text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-2xl flex items-center justify-center mb-6 border border-red-500/50 animate-pulse">
            <X className="text-red-500" size={32} />
          </div>
          <h1 className="text-2xl font-black uppercase tracking-tighter mb-2">Protocol Failure</h1>
          <p className="text-gray-400 text-sm max-w-xs mb-8">The interface encountered a fatal exception. Our intelligence core is stabilizing the environment.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-8 py-3 bg-white text-[#0A0F1E] rounded-xl font-black uppercase tracking-widest text-xs active:scale-95 transition-all"
          >
            Re-Initialize Interface
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const PUBLIC_DYNAMIC_PREFIXES = ['/prices/', '/stores/cheapest-in/', '/history/', '/battle/', '/blog/', '/alternatives/', '/newsroom/', '/features/'];

// Trigger Rebuild
const App: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { getToken, isLoaded, isSignedIn, sessionId } = useAuth();
  const clerk = useClerk();

  useEffect(() => {
    const lng = new URLSearchParams(window.location.search).get('lng');
    if (lng && lng !== i18n.language) {
      i18n.changeLanguage(lng);
      useStore.getState().setLanguage(lng);
    }
  }, []);
  
  const supabaseClient = useMemo(() => {
    const template = import.meta.env.VITE_CLERK_JWT_TEMPLATE || 'supabase';
    const client = getSupabaseClient(() => getToken({ template }));
    (window as any).supabase = client;
    return client;
  }, [getToken]);

  // Migration: Move individual confirm_status_* keys into consolidated object
  useEffect(() => {
    migrateLegacyConfirmations();
  }, []);

  // The device's location from the fix below; Trending re-centres when it arrives.
  const [liveCoords, setLiveCoords] = useState<{ lat: number; lng: number } | null>(null);

  // Fetch user location address on mount / sign-in
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;

    if (typeof navigator !== 'undefined' && navigator.geolocation) {
      console.log('[App] Attempting to pre-fetch user location...');
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          console.log(`[App] Geolocation obtained: lat=${latitude}, lng=${longitude}`);
          setLiveCoords({ lat: latitude, lng: longitude });
          try {
            const address = await reverseGeocodeCoords(latitude, longitude);
            if (address) {
              console.log(`[App] Resolved user location address: ${address}`);
              setUserLocationAddress(address);
            }
          } catch (err) {
            console.error("[App] Failed to reverse geocode user location coords:", err);
          }
        },
        (error) => {
          console.warn("[App] Geolocation permission denied or failed on mount:", error);
        },
        { timeout: 10000, enableHighAccuracy: true }
      );
    }
  }, [isLoaded, isSignedIn]);

  // Core Hooks
  const { user } = useUser();

  // Tell the Supabase client who it must authenticate as, so it never falls
  // back to the anon key for a signed-in user. When Clerk reports signed out,
  // a session that is still active in Clerk's client was dropped locally (a
  // failed token refresh), not ended: PrivateRoute then offers to restore it.
  const sessionLost = useSupabaseSessionLost();
  const isSessionDroppedLocally = useCallback(() => {
    const expected = getExpectedSupabaseAuth();
    return !!expected && !!clerk.client?.sessions?.some(s => s.id === expected.sessionId && s.status === 'active');
  }, [clerk]);
  useEffect(() => {
    if (!isLoaded) return;
    if (isSignedIn && user?.id) {
      setSupabaseAuthUser(user.id, sessionId ?? null);
      return;
    }
    if (isSignedIn || !getExpectedSupabaseAuth()) return;
    if (isSessionDroppedLocally()) markSupabaseSessionLost();
    else clearSupabaseAuthUser();
  }, [isLoaded, isSignedIn, user?.id, sessionId, isSessionDroppedLocally]);

  // The plan on open: the server reconciles Clerk Billing, Clerk metadata and
  // Supabase, and its answer is applied to the store (the persisted last-known
  // plan shows until then, so a paid user never sees the Free UI flash).
  useEffect(() => {
    if (!isSignedIn || !isLoaded || !user?.id) return;
    const { tierOwnerId, resetEntitlement } = useStore.getState();
    if (tierOwnerId && tierOwnerId !== user.id) resetEntitlement();
    reconcileEntitlement(getToken, user.id);
  }, [isSignedIn, isLoaded, user?.id, getToken]);

  // Plan changes made elsewhere (Clerk, the billing webhook, period end) are pushed in.
  useEffect(() => {
    if (!isSignedIn || !isLoaded || !user?.id || !supabaseClient) return;
    return watchEntitlement(supabaseClient, getToken, user.id);
  }, [isSignedIn, isLoaded, user?.id, getToken, supabaseClient]);

  const navigate = useNavigate();
  const location = useLocation();
  const alternativesStudioParams = new URLSearchParams(location.search);
  // Preserve explicit studio tabs in existing bookmarks; default to Alternatives.
  if (!alternativesStudioParams.get('mode')) alternativesStudioParams.set('mode', 'alternative');
  const alternativesStudioTarget = `/home/admin/blog?${alternativesStudioParams.toString()}${location.hash}`;
  const metaInitialRouteRef = useRef(true);

  useEffect(() => {
    if (metaInitialRouteRef.current) {
      metaInitialRouteRef.current = false;
    } else {
      trackMeta('PageView');
    }

    const viewContentPrefixes = ['/blog', '/alternatives', '/prices/', '/stores/cheapest-in/', '/history/', '/battle/', '/newsroom'];
    const isContentPage = viewContentPrefixes.some(prefix => location.pathname.startsWith(prefix));
    if (isContentPage) {
      trackMeta('ViewContent', {
        content_name: document.title,
        content_category: location.pathname.split('/').filter(Boolean)[0] || 'content',
        content_ids: location.pathname,
        content_type: 'product',
      });
    }
  }, [location.pathname, location.search]);


  // --- SOVEREIGN PATH PURIFIER & SANITIZER ---
  // 1. Redirect unauthenticated users from protected routes
  // 2. Redirect ALL users from malformed/non-existent paths (e.g. /band)
  useEffect(() => {
    if (!isLoaded) return;

    const isStaticPublic = PUBLIC_PATHS.includes(location.pathname);
    const isDynamicPublic = PUBLIC_DYNAMIC_PREFIXES.some(prefix => location.pathname.startsWith(prefix));
    const isPublic = isStaticPublic || isDynamicPublic;
    
    const isInternal = location.pathname.startsWith('/home');
    const isMalformed = !isPublic && !isInternal;

    // --- BYPASS CLIENT-SIDE ROUTING FOR STATIC FILES ---
    if (location.pathname === '/robots.txt' || location.pathname.startsWith('/sitemap') || location.pathname === '/llms.txt' || location.pathname.startsWith('/llms/')) {
      return;
    }
    // /demos (animation previews) is unlisted and gates itself in the render below.
    if (location.pathname === DEMOS_PATH || location.pathname === TOUR_PATH || location.pathname === AISLE_PATH) return;

    // A locally dropped session stays put: PrivateRoute offers to restore it.
    if (!isSignedIn && !isPublic && isSessionDroppedLocally()) return;
    if (!isSignedIn && !isPublic) {
      navigate(`/sign-in?redirect_url=${encodeURIComponent(location.pathname + location.search)}`, { replace: true });
    } else if (isMalformed) {
      navigate('/', { replace: true });
    }
  }, [isLoaded, isSignedIn, location.pathname, navigate, isSessionDroppedLocally]);

  const getAppSubTitle = () => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    
    // 1. Resolve Base Page Name
    let pageTitle = '';
    const path = location.pathname;
    
    if (path === '/home/settings') pageTitle = t('nav.settings');
    else if (path === '/home/history') pageTitle = t('nav.history');
    else if (path === '/home/comparison') pageTitle = t('nav.comparison');
    else if (path === '/home/categories') pageTitle = t('nav.categories');
    else if (path === '/home/account') pageTitle = t('nav.account');
    else if (path === '/home/apply') pageTitle = t('nav.enrollment');
    else if (path === '/home/admin/metrics') pageTitle = t('admin_metrics.title', { defaultValue: 'Metrics' });
    else if (path === '/home/admin/upgrade-role') pageTitle = t('nav.upgrade_role', { defaultValue: 'Upgrade Role' });
    else if (path === '/home/admin/blog') pageTitle = t('admin_navigation.content_studio', { defaultValue: 'Content Studio' });
    else if (path === '/home/admin/alternatives') pageTitle = t('admin_navigation.content_studio', { defaultValue: 'Content Studio' });
    else if (path === '/home/admin/newsroom') pageTitle = t('nav.newsroom', { defaultValue: 'Newsroom' });
    else if (path === '/home/admin') pageTitle = t('admin.terminal_title');
    else if (path === '/home') pageTitle = t('nav.home');

    if (!tab) return pageTitle;

    // 2. Resolve Tab Name Dynamically
    // Heuristic: Search for common localized key patterns
    const pageKey = path.split('/').pop() || 'home';
    const translationPaths = [
      `${pageKey}.tab_${tab}`, // e.g. account.tab_profile
      `${pageKey}.${tab}`,     // e.g. admin.architects
      `nav.${tab}`,            // e.g. nav.dashboard
      `common.${tab}`          // e.g. common.all
    ];

    let tabTitle = '';
    for (const p of translationPaths) {
      const resolved = t(p);
      if (resolved && resolved !== p) {
        tabTitle = resolved;
        break;
      }
    }

    // 3. Section Specific Edge Cases
    if (!tabTitle) {
      if (pageKey === 'comparison') {
        if (tab === 'overview') tabTitle = t('comparison.tracker');
        if (tab === 'optimizer') tabTitle = t('comparison.basket_iq');
      }
      if (pageKey === 'home') {
        if (tab === 'analytics') tabTitle = t('nav.dashboard');
        if (tab === 'logbook') tabTitle = t('nav.logbook');
      }
    }

    // 4. Fallback: Capitalize raw value
    if (!tabTitle) {
      tabTitle = tab.charAt(0).toUpperCase() + tab.slice(1);
    }

    return pageTitle ? `${tabTitle} | ${pageTitle}` : tabTitle;
  };

  // Language Geolocation Detection States
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [detectedCountryCode, setDetectedCountryCode] = useState('US');
  const [detectedCountryName, setDetectedCountryName] = useState('United States');
  const [recommendedLangCode, setRecommendedLangCode] = useState('en');

  // Country to recommended language code mapping
  const getLanguageForCountry = (countryCode: string): string => {
    const code = countryCode.toUpperCase();
    const config = getRegionalConfig(code);
    if (config && config.suggestedLanguage !== 'en') {
      return config.suggestedLanguage;
    }
    
    // Complete mapping of all countries to our 11 languages when possible
    const countryToLang: Record<string, string> = {
      // Spanish (es)
      'ES': 'es', 'MX': 'es', 'CO': 'es', 'AR': 'es', 'PE': 'es', 'VE': 'es', 'CL': 'es', 'EC': 'es',
      'GT': 'es', 'CU': 'es', 'BO': 'es', 'DO': 'es', 'HN': 'es', 'PY': 'es', 'SV': 'es', 'NI': 'es',
      'CR': 'es', 'PR': 'es', 'UY': 'es', 'PA': 'es', 'GQ': 'es',
      
      // French (fr)
      'FR': 'fr', 'BE': 'fr', 'LU': 'fr', 'MC': 'fr', 'HT': 'fr', 'MQ': 'fr', 'GP': 'fr', 'GF': 'fr',
      'PM': 'fr', 'SN': 'fr', 'ML': 'fr', 'GN': 'fr', 'CI': 'fr', 'BF': 'fr', 'NE': 'fr', 'BJ': 'fr',
      'TG': 'fr', 'CF': 'fr', 'CG': 'fr', 'GA': 'fr', 'MG': 'fr', 'CM': 'fr', 'TD': 'fr', 'DJ': 'fr',
      'NC': 'fr', 'PF': 'fr', 'WF': 'fr', 'VU': 'fr',
      
      // German (de)
      'DE': 'de', 'AT': 'de', 'CH': 'de', 'LI': 'de',
      
      // Portuguese (pt)
      'PT': 'pt', 'BR': 'pt', 'AO': 'pt', 'MZ': 'pt', 'CV': 'pt', 'GW': 'pt', 'ST': 'pt', 'TL': 'pt', 'MO': 'pt',
      
      // Japanese (ja)
      'JP': 'ja',
      
      // Chinese (zh)
      'CN': 'zh', 'TW': 'zh', 'HK': 'zh', 'SG': 'zh',
      
      // Russian (ru)
      'RU': 'ru', 'BY': 'ru', 'KZ': 'ru', 'KG': 'ru', 'UA': 'ru', 'UZ': 'ru', 'TM': 'ru', 'TJ': 'ru',
      'MD': 'ru', 'AM': 'ru', 'GE': 'ru', 'AZ': 'ru',
      
      // Arabic (ar)
      'SA': 'ar', 'AE': 'ar', 'EG': 'ar', 'DZ': 'ar', 'MA': 'ar', 'SD': 'ar', 'IQ': 'ar', 'YE': 'ar',
      'JO': 'ar', 'LB': 'ar', 'LY': 'ar', 'OM': 'ar', 'QA': 'ar', 'BH': 'ar', 'KW': 'ar', 'PS': 'ar',
      'SY': 'ar', 'TN': 'ar', 'MR': 'ar', 'SO': 'ar', 'ER': 'ar', 'KM': 'ar', 'EH': 'ar',
      
      // Hindi (hi)
      'IN': 'hi', 'NP': 'hi', 'BT': 'hi',
      
      // Swahili (sw)
      'KE': 'sw', 'TZ': 'sw', 'UG': 'sw', 'RW': 'sw', 'BI': 'sw', 'CD': 'sw', 'SS': 'sw'
    };

    return countryToLang[code] || 'en';
  };

  const handleLanguageSwitch = (remember: boolean) => {
    handleLanguageChange(recommendedLangCode);
    if (remember) {
      localStorage.setItem('cartlens_lang_prompted', 'true');
    }
    setShowLanguageModal(false);
  };

  const handleLanguageKeep = (remember: boolean) => {
    if (remember) {
      localStorage.setItem('cartlens_lang_prompted', 'true');
    }
    setShowLanguageModal(false);
  };

  const [showInstallModal, setShowInstallModal] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const { 
    pwaInstalled, 
    setPwaInstalled, 
    pwaInstallDismissed, 
    setPwaInstallDismissed 
  } = useStore();

  // Geolocation-based Country Language Detection
  useEffect(() => {
    // Skip on localhost — GeoIP APIs rate-limit dev traffic and serve no purpose locally
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') return;

    // 1. Path Guard: Only trigger on public routes
    const isStaticPublic = PUBLIC_PATHS.includes(location.pathname);
    const isDynamicPublic = PUBLIC_DYNAMIC_PREFIXES.some(prefix => location.pathname.startsWith(prefix));
    const isPublic = isStaticPublic || isDynamicPublic;
    if (!isPublic) return;

    // 2. Check if user already dismissed or processed this language prompt
    const hasPrompted = localStorage.getItem('cartlens_lang_prompted');
    if (hasPrompted === 'true') return;

    const detectLanguageAndCountry = async () => {
      let countryCode = '';
      let countryName = '';

      // Try ipapi.co (primary free GeoIP provider)
      try {
        const res = await fetch('https://ipapi.co/json/');
        if (res.ok) {
          const data = await res.json();
          if (data.country_code) {
            countryCode = data.country_code;
            countryName = data.country_name || data.country;
          }
        }
      } catch (e) {
        console.warn('[LanguageDetector] ipapi.co request failed, attempting fallback...', e);
      }

      // Try ipwho.is as fallback 1
      if (!countryCode) {
        try {
          const res = await fetch('https://ipwho.is/');
          if (res.ok) {
            const data = await res.json();
            if (data.country_code) {
              countryCode = data.country_code;
              countryName = data.country || data.country_name;
            }
          }
        } catch (e) {
          console.warn('[LanguageDetector] ipwho.is request failed, attempting fallback...', e);
        }
      }

      // Try freeipapi.com as fallback 2
      if (!countryCode) {
        try {
          const res = await fetch('https://freeipapi.com/api/json');
          if (res.ok) {
            const data = await res.json();
            if (data.countryCode) {
              countryCode = data.countryCode;
              countryName = data.countryName || data.country;
            }
          }
        } catch (e) {
          console.warn('[LanguageDetector] freeipapi.com request failed.', e);
        }
      }

      if (!countryCode) return;

      const recommended = getLanguageForCountry(countryCode);
      const activeLang = i18n.language?.split('-')[0] || 'en';

      // Show modal if the suggested language is different from user's current app language
      if (recommended !== activeLang) {
        setDetectedCountryCode(countryCode);
        setDetectedCountryName(countryName || countryCode);
        setRecommendedLangCode(recommended);
        setShowLanguageModal(true);
      }
    };

    // Delay run to avoid resource contention during initial landing page mount
    const timer = setTimeout(() => {
      detectLanguageAndCountry();
    }, 2000);

    return () => clearTimeout(timer);
  }, [location.pathname, i18n.language]);

  // DEBUG: Force PWA modals in development for UI testing
  const DEBUG_PWA_INSTALL = false; 
  const DEBUG_PWA_UPDATE = false; // Set to true to verify the update prompt UI

  // PWA Install & Update Protocol
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
    
    // Check if recently dismissed
    const dismissCooldown = 7 * 24 * 60 * 60 * 1000; // 7 days
    const isDismissed = pwaInstallDismissed && (Date.now() - pwaInstallDismissed < dismissCooldown);

    if (DEBUG_PWA_INSTALL) {
      setShowInstallModal(true);
    }

    const handleBeforeInstallPrompt = (e: any) => {
      // If already in standalone mode, or we know it's installed, or user dismissed recently, don't show custom UI
      if (isStandalone || pwaInstalled || isDismissed) return;
      
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallModal(true);
    };

    const handleAppInstalled = () => {
      console.log('PWA was installed');
      setPwaInstalled(true);
      setShowInstallModal(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

  return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [pwaInstalled, pwaInstallDismissed, setPwaInstalled]);

  const handleInstallPWA = async () => {
    if (!deferredPrompt) {
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const isMac = /Mac|iPhone|iPod|iPad/.test(navigator.platform);
      
      if (isSafari && isMac) {
        showNotification(t('pwa.safari_manual_install'), { type: 'info', duration: 6000 });
      } else {
        showNotification(t('pwa.manual_install'), { type: 'info', duration: 6000 });
      }
      setPwaInstallDismissed(Date.now());
      setShowInstallModal(false);
      return;
    }

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        console.log('User accepted the PWA install');
        setPwaInstalled(true);
      } else {
        setPwaInstallDismissed(Date.now());
      }
    } catch (err) {
      console.error("PWA Install Error:", err);
    } finally {
      setDeferredPrompt(null);
      setShowInstallModal(false);
    }
  };

  const handleDismissInstall = () => {
    setPwaInstallDismissed(Date.now());
    setShowInstallModal(false);
  };

  const swRegistrationRef = React.useRef<ServiceWorkerRegistration | null>(null);

  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker
  } = useRegisterSW({
    onOfflineReady() {
      if (import.meta.env.DEV) return;
      console.log('[PWA] Content is cached for offline use.');
      setOfflineReady(true);
    },
    onNeedRefresh() {
      if (import.meta.env.DEV) return;
      // Fired by workbox-window when it detects a waiting SW via its own event chain.
      console.log('[PWA] workbox-window: new version waiting, showing update modal.');
      setNeedRefresh(true);
    },
    onRegistered(r) {
      if (!r || import.meta.env.DEV) return;
      swRegistrationRef.current = r;
      console.log('[PWA] Service Worker registered.');

      // 1. SW was already waiting before this page loaded (e.g. another tab triggered the update).
      if (r.waiting) {
        console.log('[PWA] SW already waiting on registration.');
        setNeedRefresh(true);
      }

      // 2. Direct event-driven listener — works regardless of how workbox-window classifies the update.
      //    When r.update() fetches a new sw.js, 'updatefound' fires, then the installing worker
      //    fires 'statechange' when it finishes installing (state === 'installed' = waiting).
      r.addEventListener('updatefound', () => {
        const newWorker = r.installing;
        if (!newWorker) return;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            console.log('[PWA] New SW installed and waiting, showing update modal.');
            setNeedRefresh(true);
          }
        });
      });
    },
    onRegisterError(error) {
      if (import.meta.env.DEV) return;
      console.error('[PWA] SW registration error', error);
    }
  });

  // Trigger r.update() on a sane cadence rather than every second.
  // Fires: (a) when the user returns to the tab, (b) every 10 minutes while active.
  React.useEffect(() => {
    if (import.meta.env.DEV) return;
    
    const checkForUpdate = () => {
      const r = swRegistrationRef.current;
      if (!r || !navigator.onLine) return;
      r.update().catch(() => {});
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') checkForUpdate();
    };

    document.addEventListener('visibilitychange', onVisibility);
    const interval = setInterval(checkForUpdate, 10 * 60 * 1000); // every 10 min

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(interval);
    };
  }, []);

  const handleUpdateProtocol = async () => {
    console.log('[PWA] Initiating update protocol...');
    // 1. Tell SW to skip waiting and take control
    await updateServiceWorker(true);
    
    // 2. Force a hard reload to ensure we get the latest assets from the new SW
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;

  const [isSyncingToCloud, setIsSyncingToCloud] = useState(false);

  // Global State
  const { 
    items, latticeItems, operatingContext: storeContext, setOperatingContext: setStoreContext, itemsMetadata, addItem, removeItem, updateItem, clearBasket, 
    categories, setCategories,
    history: historyRaw, setHistory,
    userProfile, setUserProfile,
    isProUser, isUltraUser,
    isSyncing,
    currency, setCurrency,
    language, setLanguage,
    analytics, setAnalytics,
    basketInsight, setBasketInsight,
    masterInsight, setMasterInsight,
    isInsightOnCooldown, cooldownTimeLeft,
    lastHistoryUpdate, lastSettingsUpdate, lastCategoriesUpdate,
    lastBasketChangeTimestamp, lastInsightGeneratedTimestamp, setLastInsightGeneratedTimestamp,
    lastTripSavedTimestamp,
    latticeParticipation, setLatticeParticipation,
    tripDateTime, setTripDateTime, isTimeManual, setIsTimeManual,
    receiptImageUrl, setReceiptImageUrl,
    receiptSubtotal, setReceiptSubtotal,
    unitSystem, massUnits,
    load, saveItemImage, syncLocalSettings,
    isCinematicLoading, appLoadingProgress,
    handleBypassCooldown, spendCredits,
    syncStateToCloud, refreshAccount,
    hydrated,
    seenBulkImportBatchIds, markBulkImportBatchSeen,
  } = useStore();

  const isAdmin = userProfile?.role === 'admin';

  // Cookie consent: load the trackers each category allows (src/lib/consent.ts).
  const cookieConsent = useStore(s => s.cookieConsent);
  const consentOptInRequired = useStore(s => s.consentOptInRequired);
  const analyticsAllowed = effectiveConsent(cookieConsent, consentOptInRequired).analytics;
  useEffect(() => {
    loadCookieless();
    fetchConsentPolicy().then(required => useStore.getState().setConsentOptInRequired(required));
  }, []);
  useEffect(() => { applyConsent(cookieConsent, consentOptInRequired); }, [cookieConsent, consentOptInRequired]);
  useEffect(() => { if (analyticsAllowed) enableSentryReplay(); }, [analyticsAllowed]);

  // Signed-in users keep their choice in profiles.settings.privacy.cookie_consent.
  // The store adopts a newer profile copy on load; the sync sends the local choice
  // whenever it differs from the loaded profile (chosen while signed out, or just changed).
  useEffect(() => {
    if (!isSignedIn || !user?.id || !userProfile?.id || !supabaseClient) return;
    if (!cookieConsent.decidedAt) return;
    void syncStateToCloud(supabaseClient, user.id);
  }, [isSignedIn, user?.id, userProfile?.id, cookieConsent, supabaseClient, syncStateToCloud]);

  useEffect(() => {
    if (!isSignedIn || !user?.id || !analyticsAllowed) return;
    if (typeof window.gtag === 'function') window.gtag('set', { user_id: user.id });
  }, [isSignedIn, user?.id, analyticsAllowed]);

  // The profile exists before the attribution insert, and the insert's primary
  // key ensures StrictMode/reloads cannot send duplicate sign_up events.
  useEffect(() => {
    if (!isSignedIn || !user?.id || !userProfile?.id || !supabaseClient) return;
    recordSignup(supabaseClient, user.id, user.createdAt).catch(
      error => console.warn('[Acquisition] Signup tracking failed:', error)
    );
  }, [isSignedIn, user?.id, user?.createdAt, userProfile?.id, supabaseClient]);

  const handleSyncToCloud = useCallback(async () => {
    if (!supabaseClient || !user?.id) return;
    setIsSyncingToCloud(true);
    try {
      await syncStateToCloud(supabaseClient, user.id);
    } catch (e) {
      console.warn("[Sync] Background sync failed, will retry later:", e);
    } finally {
      // Visual polish delay
      setTimeout(() => setIsSyncingToCloud(false), 1000);
    }
  }, [supabaseClient, user?.id, syncStateToCloud]);

  // AUTO-SYNC TRIGGER
  useEffect(() => {
    if (lastBasketChangeTimestamp > 0) {
      const timer = setTimeout(handleSyncToCloud, 3000);
      return () => clearTimeout(timer);
    }
  }, [lastBasketChangeTimestamp, handleSyncToCloud]);

  const { 
    isMenuOpen, setIsMenuOpen,
    isPanelMinimized, setIsPanelMinimized,
    showStoreModal, setShowStoreModal,
    showAddModal, setShowAddModal,
    isInsightModalOpen, setIsInsightModalOpen,
    isChatOpen, setIsChatOpen,
    notification, setNotification,
    processingCount, setProcessingCount,
    isCameraOpen, setIsCameraOpen,
    isBulkImportOpen, setIsBulkImportOpen,
    scanMode, setScanMode,
    showDatePicker, setShowDatePicker,
    previewImageUrl, previewReceiptZoom, setPreviewImageUrl,
    showAuthModal, setShowAuthModal,
    showClearConfirm, setShowClearConfirm
  } = useUIStore();

  const [maintenance, setMaintenance] = useState({ active: false, excerpt: "", content: "" });
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);

  useEffect(() => {
    fetch("/api/maintenance/status")
      .then(r => r.json())
      .then(d => setMaintenance(d))
      .catch(() => {});
  }, []);

  const isSystemAdmin = user?.publicMetadata?.role === 'admin';
  const handleAuthTrigger = useCallback((show: boolean = true) => {
    if (!show) {
      setShowAuthModal(false);
      return;
    }

    if (maintenance?.active && !isSystemAdmin) {
      setShowMaintenanceModal(true);
    } else {
      setShowAuthModal(true);
    }
  }, [maintenance?.active, isSystemAdmin, setShowAuthModal, setShowMaintenanceModal]);


  const [globalReceiptEntry, setGlobalReceiptEntry] = useState<ShoppingHistoryEntry | null>(null);
  const [isGlobalReceiptOpen, setIsGlobalReceiptOpen] = useState(false);

  // Manual Page View Tracking for Google Analytics (SPA support)
  useEffect(() => {
    // Only track once authentication is loaded and cinematic loading has finished
    if (isLoaded && !isCinematicLoading && analyticsAllowed && typeof window.gtag === 'function') {
      
      // --- ANALYTICS PATH VALIDATION ---
      const isPublic = PUBLIC_PATHS.includes(location.pathname);
      const isDynamicPublic = PUBLIC_DYNAMIC_PREFIXES.some(prefix => location.pathname.startsWith(prefix));
      const isPublicPath = isPublic || isDynamicPublic;
      
      const isInternal = isSignedIn && location.pathname.startsWith('/home');
      
      // 1. Path Guard: Don't track if unauthenticated on a non-public path (wait for Purifier)
      if (!isSignedIn && !isPublicPath) return;
      
      // 2. Structural Guard: Don't track malformed or non-existent top-level routes (e.g. /band)
      // Even if signed in, we only track recognized app namespaces
      if (!isPublicPath && !isInternal) return;

      // 3. Transient Guard: Don't track the root '/' if we are about to redirect to a dashboard
      if (isSignedIn && location.pathname === '/') return;

      const timer = setTimeout(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        
        // Transform query parameters into clean virtual paths for GA4 reporting
        // This ensures tabs are reported as distinct "pages" (e.g. /home/account/profile)
        let virtualPath = location.pathname;
        if (tab) {
          virtualPath = `${location.pathname.replace(/\/$/, '')}/${tab}`;
        }
        
        // Construct a virtual URL to force GA4 to parse the virtual path as the true location
        // GA4 needs the campaign parameters on the first virtual page view.
        // Do not include unrelated query parameters, which may contain private data.
        const campaignParams = new URLSearchParams();
        for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
          const value = params.get(key);
          if (value) campaignParams.set(key, value);
        }
        const virtualLocation = window.location.origin + virtualPath
          + (campaignParams.size ? `?${campaignParams.toString()}` : '');

        // Configure GA4 and explicitly send the page_view event with the virtual path
        // (Since send_page_view: false is set in index.html, config alone won't trigger a hit)
        (window as any).gtag('config', 'G-5RPZFLP419', {
          send_page_view: false,
          page_path: virtualPath,
          page_location: virtualLocation,
          page_title: document.title || 'CartLens',
          cookie_domain: 'auto'
        });
        
        // Manually trigger the page view to ensure it registers across all environments
        (window as any).gtag('event', 'page_view', {
          page_path: virtualPath,
          page_location: virtualLocation,
          page_title: document.title || 'CartLens'
        });
      }, 300); // Increased buffer to ensure DOM and routing stability

      return () => clearTimeout(timer);
    }
  }, [location, isLoaded, isCinematicLoading, isSignedIn, analyticsAllowed]);

  // REGISTER GLOBAL UI ACTIONS (Bridge AI assistant to Modals)
  useEffect(() => {
    const { registerAction, unregisterAction } = useStore.getState();
    
    registerAction('VIEW_DIGITAL_RECEIPT', (entry: ShoppingHistoryEntry) => {
      setGlobalReceiptEntry(entry);
      setIsGlobalReceiptOpen(true);
    });
    
    registerAction('VIEW_ORIGINAL_RECEIPT', (entry: ShoppingHistoryEntry) => {
      let receiptUrl = entry.receiptImageUrl;
      if (!receiptUrl && entry.items.length > 1) {
        const urlCounts: Record<string, number> = {};
        entry.items.forEach(i => { if(i.imageUrl) urlCounts[i.imageUrl] = (urlCounts[i.imageUrl] || 0) + 1; });
        const sharedUrl = Object.entries(urlCounts).find(([url, count]) => count > 1)?.[0];
        if (sharedUrl) receiptUrl = sharedUrl;
      }
      if (!receiptUrl) {
        receiptUrl = entry.items.find(i => i.imageUrl?.includes('receipt'))?.imageUrl;
      }
      if (receiptUrl) {
        setPreviewImageUrl(receiptUrl);
      }
    });

    registerAction('SAVE_TO_HISTORY', () => {
      console.log("[App] Chat triggered SAVE_TO_HISTORY");
      setShowArchiveModal(true);
    });

    registerAction('ARCHIVE_BASKET', () => {
      console.log("[App] Chat triggered ARCHIVE_BASKET");
      setShowArchiveModal(true);
    });

    return () => {
      unregisterAction('VIEW_DIGITAL_RECEIPT');
      unregisterAction('VIEW_ORIGINAL_RECEIPT');
      unregisterAction('SAVE_TO_HISTORY');
      unregisterAction('ARCHIVE_BASKET');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setPreviewImageUrl]);


  useSubscription(supabaseClient);

  const handleRemoveItem = useCallback(async (id: string) => {
    const itemToRemove = activeItems.find(i => i.id === id);
    const localImageUrl = itemToRemove?.imageUrl;
    removeItem(id);
    if (localImageUrl && localImageUrl.startsWith('local:')) {
      const stillUsed = activeItems.some(i => i.id !== id && i.imageUrl === localImageUrl);
      if (!stillUsed) {
        const { ImageCacheService } = await import('./services/imageCacheService');
        await ImageCacheService.removeImage(localImageUrl);
      }
    }
  }, [items, removeItem]);

  const handleCloseHistory = useCallback(() => {
    navigate('/home');
  }, [navigate]);

  const handlePreviewImage = useCallback((url: string) => setPreviewImageUrl(url), [setPreviewImageUrl]);

  // Products the user rebuys, most urgent first (Restock Radar + Suggested Essentials).
  const restockSettings = userProfile?.settings?.restock;
  const { data: restockSignals } = useQuery({
    queryKey: queryKeys.restockSignals(user?.id || ''),
    queryFn: () => getRestockSignals(supabaseClient!, user!.id),
    staleTime: 5 * 60 * 1000,
    enabled: !!supabaseClient && !!user?.id,
  });
  const restockItems = useMemo(() => computeRestockItems(
    analytics?.item_data,
    Array.isArray(historyRaw) ? historyRaw : [],
    { settings: restockSettings, aiProfile: userProfile?.ai_profile, signals: restockSignals },
  ), [historyRaw, analytics?.item_data, restockSettings, userProfile?.ai_profile, restockSignals]);

  const pantrySummary = useMemo(() => {
    if (restockItems.length === 0) return "{}";
    const due = restockItems.filter(l => l.daysUntilDue <= 3).map(l => ({ n: l.name, d: l.daysUntilDue }));
    return JSON.stringify({ c: due.length, l: due.slice(0, 10), t: restockItems.length });
  }, [restockItems]);

  // Local UI State
  const [isSyncingSubscription, setIsSyncingSubscription] = useState(false);
  const [isHardSyncing, setIsHardSyncing] = useState(false);
  const archiveInFlight = useRef(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState<{ show: boolean; feature: string; tier?: 'Pro' | 'Ultra' }>({ show: false, feature: '' });

  // History Pagination State
  const [isFetchingHistory, setIsFetchingHistory] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [userLocationAddress, setUserLocationAddress] = useState<string | null>(null);
  const historyOffset = useRef(0);

  const isLatticeMode = isAdmin && isLatticeScanRoute(location.pathname, location.search);
  const routeBasketContext = isAdmin ? basketContextForRoute(location.pathname, location.search) : 'personal';
  const isPersonnelScanner = routeBasketContext === 'personnel';

  // The route selects the basket before paint; navigation never toggles basket state.
  useLayoutEffect(() => {
    const desiredContext = routeBasketContext;
    if (storeContext !== desiredContext) {
      setStoreContext(desiredContext);
    }
  }, [routeBasketContext, storeContext, setStoreContext]);

  const loadMoreHistory = useCallback(async () => {
    if (!supabaseClient || !user?.id || isFetchingHistory || !hasMoreHistory) return;
    setIsFetchingHistory(true);
    try {
      const more = await getHistory(supabaseClient, user.id, 20, historyOffset.current);
      if (more.length < 20) setHasMoreHistory(false);
      setHistory(prev => [...prev, ...more]);
      historyOffset.current += more.length;
    } catch (err) {
      console.error("Failed to load more history:", err);
    } finally {
      setIsFetchingHistory(false);
    }
  }, [supabaseClient, user?.id, isFetchingHistory, hasMoreHistory, setHistory]);

  const sortedHistory = useMemo(() => 
    [...historyRaw].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  , [historyRaw]);

  const showNotification = useCallback((message: string, options: { type: 'success' | 'error' | 'warning' | 'info', duration?: number }) => {
    setNotification({ id: Date.now(), message, type: options.type, duration: options.duration || 4000 });
  }, [setNotification]);

  // Bulk Import: topbar icon dot + background-completion toast. Runs independently
  // of BulkImportView being mounted, so the icon stays accurate app-wide.
  const bulkImportStatus = useBulkImportStatusIndicator(supabaseClient, user?.id);
  const bulkImportBatchSeen = !bulkImportStatus.batchId || seenBulkImportBatchIds.includes(bulkImportStatus.batchId);
  const bulkImportIndicatorColor = useMemo((): 'processing' | 'done' | 'failed' | null => {
    if (!bulkImportStatus.batchId) return null;
    if (bulkImportStatus.status === 'processing') return 'processing';
    if (bulkImportBatchSeen) return null;
    if (bulkImportStatus.status === 'blocked' || bulkImportStatus.hasFailure) return 'failed';
    if (bulkImportStatus.status === 'complete') return 'done';
    return null;
  }, [bulkImportStatus, bulkImportBatchSeen]);

  // Acknowledge only settled results. Active work stays visible even after opening.
  useEffect(() => {
    if (isBulkImportOpen && bulkImportStatus.batchId && bulkImportStatus.status !== 'processing' && !bulkImportBatchSeen) {
      markBulkImportBatchSeen(bulkImportStatus.batchId);
    }
  }, [isBulkImportOpen, bulkImportStatus.batchId, bulkImportStatus.status, bulkImportBatchSeen, markBulkImportBatchSeen]);

  const prevBulkImportStatusRef = useRef<string | null>(null);
  useEffect(() => {
    const { batchId, status, hasFailure } = bulkImportStatus;
    if (!batchId || !status) { prevBulkImportStatusRef.current = null; return; }
    const wasProcessing = prevBulkImportStatusRef.current === 'processing';
    prevBulkImportStatusRef.current = status;
    const isTerminal = status === 'complete' || status === 'blocked';
    if (wasProcessing && isTerminal && !isBulkImportOpen) {
      const isFailure = status === 'blocked' || hasFailure;
      showNotification(
        isFailure ? t('bulk_import.notify_failed') : t('bulk_import.notify_complete'),
        { type: isFailure ? 'error' : 'success' }
      );
      markBulkImportBatchSeen(batchId);
      // BulkImportView isn't mounted to fire onArchiveComplete itself here —
      // refresh profile/credits/history directly so the topbar total is still
      // correct next time the user looks, not just after a manual page refresh.
      if (supabaseClient && user?.id) load(supabaseClient, user.id, false);
    }
  }, [bulkImportStatus, bulkImportBatchSeen, isBulkImportOpen, showNotification, markBulkImportBatchSeen, t, supabaseClient, user?.id, load]);

  // Merchant Integration
  const {
    currentStore,
    nearbyStores,
    isDetectingStore,
    isSearchingStores,
    userCoords,
    selectStore: selectStoreRaw,
    detectStore,
    searchForStores,
    setNearbyStores
  } = useMerchant();

  // Trip Budget (PERSONAL_NOTES.md#trip-budget): the budget sheet opens after an
  // explicit store pick, or before a capture action when no budget is set yet.
  const [isBudgetSheetOpen, setIsBudgetSheetOpen] = useState(false);
  const pendingBudgetActionRef = useRef<(() => void) | null>(null);
  const [receiptDatePending, setReceiptDatePending] = useState(false);
  const pendingReceiptBudgetRef = useRef(false);
  const budgetPromptOn = userProfile?.settings?.budget?.prompt_on_store_select !== false;

  // Only explicit picks come through here (store picker, manual entry); auto-detected
  // and assistant-set stores use selectStoreRaw / setCurrentStore and never prompt.
  const selectStore = useCallback(async (store: StoreInfo) => {
    const formatted = await selectStoreRaw(store);
    if (formatted) {
      setShowStoreModal(false);
      showNotification(t('chat.merchant_set', { name: formatted.name }), { type: 'success', duration: 3000 });
      if (!isLatticeMode && budgetPromptOn && useStore.getState().tripBudget == null) {
        if (receiptDatePending) pendingReceiptBudgetRef.current = true;
        else setIsBudgetSheetOpen(true);
      }
    }
  }, [selectStoreRaw, showNotification, t, setShowStoreModal, isLatticeMode, budgetPromptOn, receiptDatePending]);

  const [storePickerMode, setStorePickerMode] = useState<'list' | 'map' | 'manual'>('list');
  const [manualStoreSearch, setManualStoreSearch] = useState('');
  const [manualStoreName, setManualStoreName] = useState('');
  const [manualStoreAddress, setManualStoreAddress] = useState('');
  const [isGeocodingStore, setIsGeocodingStore] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const lastSelectedAddressRef = useRef<string | null>(null);
  // The autocomplete pick, so the store is looked up by its Google place ID while the fields still match it.
  const selectedPlaceRef = useRef<{ placeId?: string; name: string; address: string } | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const receiptLocationSequence = useRef(0);

  const applyReceiptMetadata = useCallback(async (result: {
    storeName?: string;
    storeAddress?: string;
    storeAddressNormalized?: string;
    date?: string;
    time?: string;
    subtotal?: number;
    totalAmount?: number;
    lat?: number;
    lng?: number;
    skipStoreResolution?: boolean;
  }) => {
    if (result.subtotal) {
      setReceiptSubtotal(result.subtotal);
    }

    if (!result.skipStoreResolution) {
      setShowDatePicker(false);
      pendingReceiptBudgetRef.current = false;
      setReceiptDatePending(!parseReceiptDate(result.date));
      const sequence = ++receiptLocationSequence.current;
      // Clear the previous branch before lookup so this receipt cannot inherit it.
      useStore.getState().setCurrentStore(null);
      const resolved = await resolveReceiptLocation(result, {
        directory: (name, address) => supabaseClient ? resolveReceiptStore(supabaseClient, name, address) : Promise.resolve(null),
        geocode: async (name, address, normalized) => {
          try {
            const geo = await geocodeStore(name, address);
            if (geo) return geo;
          } catch { /* fall back to the printed address geocoder */ }
          return geocodeReceiptStore(name, address, normalized, userCoords.current);
        },
      });
      // A newer scan or an explicit store pick wins over a slow lookup.
      if (sequence !== receiptLocationSequence.current) return;
      if (!useStore.getState().currentStore) {
        if (resolved) {
          await selectStoreRaw(resolved);
        } else {
          setManualStoreName(result.storeName || '');
          setManualStoreAddress(result.storeAddress || '');
          setManualStoreSearch(result.storeName || '');
          setAddressSuggestions([]);
          selectedPlaceRef.current = null;
          setStorePickerMode('manual');
          setShowStoreModal(true);
        }
      }
    }

    if (parseReceiptDate(result.date)) {
      try {
        const newDate = applyReceiptDateTime(new Date(useStore.getState().tripDateTime), result.date, result.time);
        setTripDateTime(newDate);
        setIsTimeManual(true);
      } catch (e) {
        console.error("[Metadata] Date sync failed:", e);
      }
    }
  }, [selectStoreRaw, userCoords, setShowStoreModal, setShowDatePicker, setTripDateTime, setIsTimeManual, supabaseClient]);

  // Receipt corrections are sequential: store first, then date/time. This
  // also observes store choices made through CartAssistant's direct setters.
  useEffect(() => {
    if (!receiptDatePending || !currentStore || showStoreModal || isBudgetSheetOpen) return;
    setReceiptDatePending(false);
    setShowDatePicker(true);
  }, [receiptDatePending, currentStore, showStoreModal, isBudgetSheetOpen, setShowDatePicker]);

  const closeDatePicker = useCallback(() => {
    setShowDatePicker(false);
    if (pendingReceiptBudgetRef.current) {
      pendingReceiptBudgetRef.current = false;
      if (useStore.getState().currentStore && useStore.getState().tripBudget == null) setIsBudgetSheetOpen(true);
    }
  }, [setShowDatePicker]);

  // Stable user key for CartAssistant thread persistence
  const stableUserKey = useMemo(() => user?.id, [user?.id]);

  useEffect(() => {
    liveState.applyReceiptMetadata = applyReceiptMetadata;
  }, [applyReceiptMetadata]);

  const getCartAssistantSystemPrompt = useCallback((lang: string = 'en') => {
    const signatureSummary = getProductSignatureRuleSummary(lang);
    const languageName = getLanguageName(lang);

    return buildSystemPrompt({
      persona: `CartLens AI-powered shopping intelligence assistant (CartAssistant), helping with shopping and the user's profile. Retail price discovery uses only CartLens tools; account/profile questions use the profile tool and LIVE APP CONTEXT currentProfile. Your data sources are, in priority order: (1) the CartLens Geospatial Lattice — real GPS-verified receipts from nearby shoppers, and (2) Live Market Discovery — CartLens's own regional store-price lookup (\`discover_live_market_products\` / \`get_local_offers_for_item\`), used only as a fallback when the Lattice has no or thin local coverage. You have NO general knowledge about pricing, services, markets, or the internet — NEVER answer from training data, and NEVER tell the user to go check a website or store themselves; every price you report must come from one of those two CartLens tools. For any "where to buy" or "find cheaper" query: call get_user_location, then search_global_lattice. If the Lattice has no data — or the user is discovering a product not yet in their basket or history — fall back to discover_live_market_products and clearly label those results as regional estimates, not GPS-verified prices. For "is this a good price" or "find a better deal" on an item already in the basket, use get_local_offers_for_item. Only if BOTH the Lattice and Live Market Discovery return nothing should you say: "No price data found near you." Anything about the user's OWN purchases, receipts, baskets, spending or shopping history — including charting or mapping it — is always in scope; answer it with the history/spending tools and cards. If the query is outside both CartLens account/profile support and physical retail (e.g. digital services, flights, financial products), respond ONLY with: "CartLens can only search for physical retail products you'd find on a store shelf or receipt. Try scanning a product or asking about grocery, pharmacy, or household items."`,
      protocol: [
        PROFILE_QUESTION_RULE,
        PERSONAL_EVIDENCE_RULE,
        '1. IMAGE HANDLING — MANDATORY: When the user uploads ANY image, ALWAYS call `process_uploaded_image` FIRST. It auto-detects receipt vs product photo — do NOT pre-judge the image type yourself.',
        '   - If isReceipt=true: call `set_receipt_metadata` (pass lat/lng from process_uploaded_image result) → render `ReceiptMetadataCard` → WAIT for user to click "Extract Items" → then call `add_all_items_to_basket` with NO arguments (items are staged internally).',
        '   - If isReceipt=false: the tool already returns status="AWAITING_VISUAL_CONFIRMATION_CARD" with a nonce and items. MANDATORY: render ConfirmActionCard immediately using the nonce, action, toolName, and items from the result. After user confirms, call `add_all_items_to_basket` with NO arguments.',
        '   NEVER call `add_all_items_to_basket` in the same turn as `process_uploaded_image` or `set_receipt_metadata`. You MUST wait for the user to confirm via the UI components.',
        '   NEVER use your own vision to read an image. NEVER skip `process_uploaded_image` and go straight to `process_receipt_image` or `process_item_image`.',
        '   IMAGE RESOLUTION: To process an image, use the URL provided in the `lastUploadedImage` context helper. If multiple images exist, the context contains the most recent one.',
        '   IMAGE RETRY RULE: If processing fails, use the IndexedDB URL from context to retry WITHOUT asking the user to re-upload.',
        '2. VISION: Use structured extraction for discrete fields: brand, product, variety, size, unit.',
        '   - NOMENCLATURE: ' + signatureSummary,
        '3. DATA: Confirm ALL changes. For manually-described items (no image): add one at a time via add_single_item_to_current_basket. For image/receipt items: ALWAYS use add_all_items_to_basket — NEVER add_single_item_to_current_basket. Include imageUrl.',
        '4. SNAPSHOT: ALWAYS pass all external data (history, coordinates, lists) into visual component "props" for persistence.',
        '5. UI: Use components like MerchantMapCard for geospatial data and HistoryListCard for trips.',
        '6. PANTRY: Suggest restock based on "pantry_status".',
        '7. LATTICE SEARCH: For ANY query about where to buy something, find something cheaper, compare prices, or price discovery — ALWAYS call `get_user_location` then `search_global_lattice`. This includes: "where can I buy X cheaper", "where is the cheapest X", "find me X near me", "is X cheaper somewhere else", or any variant. If `search_global_lattice` returns no results — or the user is discovering/searching for a product not yet in their basket or history — IMMEDIATELY call `discover_live_market_products` in the SAME turn (reuse the lat/lng/address from `get_user_location`) and render `LiveMarketDiscoveryCard`. NEVER stop to ask the user whether to run Live Market Discovery — it is the automatic fallback, not an optional extra. Clearly tell the user these are regional estimates, not GPS-verified Lattice prices. When the user names a specific retailer (e.g. "compare to Kroger"), still run the discovery and call out that retailer price if it appears; if it does not, say so and show the closest alternatives. To add a discovered result, call `add_market_product_to_basket` with its productIndex and chosen storeName. NEVER answer price or availability questions from your own training knowledge, and NEVER suggest an external website or service — the Lattice and Live Market Discovery are the ONLY sources of truth, both native CartLens tools. Only use `get_lattice_insights` if you already have a verified `canonicalId` (slug).',
        '8. LOCAL OFFERS: When the user asks if a specific basket item is a good price, wants a better deal, or asks "where\'s this cheaper nearby" about an item already in the basket, call `get_local_offers_for_item` (pass lat/lng from `get_user_location` if the item hasn\'t been grounded yet) and render `LocalOffersCard`. This is the same data shown behind the info icon on that basket item — do not use `search_global_lattice` or `discover_live_market_products` for this case.',
        '9. SHELF UNIT PRICE: When an image tool result includes a SHELF UNIT-PRICE CHECK, say it in your reply as the tool instructs. When the user asks whether a basket item\'s unit price is right (or tells you what the tag says, e.g. "the tag says $0.31/oz"), call `check_unit_price`. Word a mismatch neutrally ("the unit price doesn\'t add up") with both figures and the formula; never call it a store error or an overcharge. If the user says the tag is right (the size was misread), call `check_unit_price` with applySizeFix=true to set the size the tag implies.',
        '10. REPEAT TRIP RULE: When repeating or re-adding items from a historical trip, ALWAYS call the atomic tool `readd_trip_to_basket` with the entryId. This tool handles setting the store and appending all items (including images) in one step. DO NOT call get_shopping_trip_details, set_current_store, or request_confirmation for this flow. DO NOT clear the basket. The user wants an instant append of the old items to their current shopping session.',
      ],
      rules: [
        'IMAGE RULE: process_uploaded_image → if receipt: set_receipt_metadata → ReceiptMetadataCard → WAIT; if product: render ConfirmActionCard (nonce+items already in tool result) → WAIT. NEVER add items before confirmation.',
        'BASKET RULE: Append items to current basket by default. Only clear the basket if the user specifically asks to "clear", "wipe", or "replace" the basket.',
        'BASKET LOCK: You are strictly FORBIDDEN from clearing the basket during a "repeat trip" or "re-add" flow unless the user explicitly confirms they want to replace their existing items.',
        'RE-ADD RULE: Use `readd_trip_to_basket` for an atomic, confirmed restoration. NO confirmation cards, NO per-item additions, NO basket clears.',
        'MERCHANT RULE: When setting a store from history, ALWAYS pass the full metadata (lat, lng, url) to set_current_store to maintain Geospatial DNA integrity.',
        'CONTEXT: Use the provided `currentStore` and `nearbyStores` context helpers to identify the merchant. Do NOT hallucinate locations.',
        'PRICE KNOWLEDGE RULE: NEVER answer "where to buy", "where is cheaper", or price/availability questions from your training data. These questions ALWAYS require a `search_global_lattice` call, then — in the same turn, without asking — `discover_live_market_products` whenever the Lattice has no data (label those results as regional estimates, not verified prices). If both return nothing, say so. Do not redirect users to external websites, search engines, or services outside CartLens\'s own tools.',
        'Use ATOMIC tools. Snapshot EVERYTHING into props. CLEAN output only. LANG: ' + languageName.toUpperCase() + '.'
      ]
    });
  }, [t]);

  const systemPrompt = useMemo(() => {
    return getCartAssistantSystemPrompt(i18n.language);
  }, [getCartAssistantSystemPrompt, i18n.language]);

  const groundingInProgress = useRef<Set<string>>(new Set());

  const [brand, setBrand] = useState<string | null>(null);
  const [product, setProduct] = useState('');
  const [variety, setVariety] = useState<string | null>(null);
  const [size, setSize] = useState<number | string | null>(null);
  const [unit, setUnit] = useState<string | null>(null);
  const [newItemPrice, setNewItemPrice] = useState('');
  const [newItemCategory, setNewItemCategory] = useState('Other');
  const [newItemImage, setNewItemImage] = useState<string | null>(null);
  const [newItemQuantity, setNewItemQuantity] = useState(1);
  const [newItemCanonicalId, setNewItemCanonicalId] = useState('');
  const [newItemEmbedding, setNewItemEmbedding] = useState<number[] | null>(null);
  const [newItemUPC, setNewItemUPC] = useState<string | null>(null);
  const [newItemGTIN, setNewItemGTIN] = useState<string | null>(null);
  const [newItemPLU, setNewItemPLU] = useState<string | null>(null);
  const [newItemMetadata, setNewItemMetadata] = useState<any>(null);
  const [newItemShelfLife, setNewItemShelfLife] = useState<number | null>(null);
  const [newItemExpirationDate, setNewItemExpirationDate] = useState<string | null>(null);
  const [isExtractingManual, setIsExtractingManual] = useState(false);
  const [isSuggestingCategory, setIsSuggestingCategory] = useState(false);
  const [manualAddCatSearch, setManualAddCatSearch] = useState('');

  const currencySymbol = useMemo(() => CURRENCIES.find(c => c.code === currency)?.symbol || '$', [currency]);
  const activeItems = useMemo(() => storeContext === 'personnel' ? latticeItems : items, [storeContext, items, latticeItems]);

  // Suggested Essentials: what's due that the user buys at this store.
  // "Price changed at this store": each basket item against the user's own
  // last purchase of it at the current store (src/lib/priceChange.ts).
  const storePriceChanges = useMemo(() => {
    const changes: Record<string, StorePriceChange> = {};
    if (!currentStore?.name || !sortedHistory.length) return changes;
    for (const item of activeItems) {
      const change = findStorePriceChange(item as any, sortedHistory as any, currentStore as any);
      if (change) changes[item.id] = change;
    }
    return changes;
  }, [activeItems, sortedHistory, currentStore]);

  const basketSuggestions = useMemo(
    () => selectBasketSuggestions(restockItems, activeItems, currentStore?.name),
    [restockItems, activeItems, currentStore?.name],
  );
  const totalBasket = useMemo(() => activeItems.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0), [activeItems]);

  // Trip Budget: remaining is derived from totalBasket, so it follows every add/remove.
  const tripBudget = useStore(s => s.tripBudget);
  const budgetState = isLatticeMode ? null : getBudgetState(tripBudget, totalBasket);
  // Budget alerts format money the way the Basket Balance meter does ("1,28 €" in Spanish, "$1.28" in English).
  const formatBudgetMoney = useCallback((value: number) => {
    try {
      return new Intl.NumberFormat(i18n.language, { style: 'currency', currency: currency || 'USD' }).format(value);
    } catch {
      return `${currencySymbol}${value.toFixed(2)}`;
    }
  }, [i18n.language, currency, currencySymbol]);

  // Runs a capture action (Full Bill, Price Tag, Import, manual entry), asking for
  // the trip budget first when none is set and the user hasn't closed the sheet this trip.
  const withBudget = useCallback((action: () => void) => {
    const { tripBudget: budget, budgetPromptDismissed } = useStore.getState();
    if (isLatticeMode || !budgetPromptOn || budget != null || budgetPromptDismissed) { action(); return; }
    pendingBudgetActionRef.current = action;
    setIsBudgetSheetOpen(true);
  }, [isLatticeMode, budgetPromptOn]);

  // amount: a number = saved, null = budget removed (editing), 'skip' = no budget this
  // trip. Each continues the action that opened the sheet.
  const finishBudgetSheet = useCallback((amount: number | null | 'skip') => {
    const store = useStore.getState();
    if (amount === 'skip' || amount === null) {
      if (amount === null) store.setTripBudget(null);
      store.setBudgetPromptDismissed(true);
    } else {
      store.setTripBudget(amount);
    }
    setIsBudgetSheetOpen(false);
    const pending = pendingBudgetActionRef.current;
    pendingBudgetActionRef.current = null;
    pending?.();
  }, []);

  // X / backdrop: cancels the action that opened the sheet; the sheet asks again next time.
  const closeBudgetSheet = useCallback(() => {
    pendingBudgetActionRef.current = null;
    setIsBudgetSheetOpen(false);
  }, []);

  // One alert when the basket crosses into orange (80%) or over the budget.
  const prevBudgetStatusRef = useRef<BudgetStatus | null>(null);
  useEffect(() => {
    const status = budgetState?.status ?? null;
    const prev = prevBudgetStatusRef.current;
    prevBudgetStatusRef.current = status;
    if (!budgetState || !prev) return;
    const rank: Record<BudgetStatus, number> = { ok: 0, warn: 1, over: 2 };
    if (rank[budgetState.status] <= rank[prev]) return;
    if (budgetState.status === 'warn') {
      showNotification(t('budget.toast_warn', { percent: Math.round(budgetState.ratio * 100), budget: formatBudgetMoney(budgetState.budget) }), { type: 'warning' });
    } else {
      showNotification(t('budget.toast_over', { amount: formatBudgetMoney(-budgetState.remaining) }), { type: 'error' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgetState?.status]);
  const isGeneratingInsight = useMemo(() => isInsightOnCooldown && !basketInsight, [isInsightOnCooldown, basketInsight]);

  // Free plan: 3 archived personal trips per rolling 30 days, counted by the
  // database (free_archive_allowance; archive_trip enforces the same rule).
  // Until it has answered, nothing is locked.
  const [archiveAllowance, setArchiveAllowance] = useState<ArchiveAllowance | null>(null);
  const refreshArchiveAllowance = useCallback(async () => {
    if (!supabaseClient || !user?.id) return;
    try {
      setArchiveAllowance(await getArchiveAllowance(supabaseClient, user.id));
    } catch (err) {
      console.warn('[App] Archive allowance unavailable:', err);
    }
  }, [supabaseClient, user?.id]);
  useEffect(() => { refreshArchiveAllowance(); }, [refreshArchiveAllowance, isProUser, isUltraUser]);
  // When the allowance is used up, check again the moment the next archive frees up.
  useEffect(() => {
    const at = archiveAllowance?.nextSlotAt ? new Date(archiveAllowance.nextSlotAt).getTime() : null;
    if (!at) return;
    const timer = setTimeout(refreshArchiveAllowance, Math.min(Math.max(at - Date.now(), 0) + 1000, 2 ** 31 - 1));
    return () => clearTimeout(timer);
  }, [archiveAllowance?.nextSlotAt, refreshArchiveAllowance]);

  const isUsageLimitReached = useMemo(() => {
    if (isPersonnelScanner) return false;
    if (isProUser || isUltraUser) return false;
    return !!archiveAllowance && !archiveAllowance.unlimited && archiveAllowance.remaining <= 0;
  }, [isProUser, isUltraUser, archiveAllowance, isPersonnelScanner]);
  // Shown to free users who still have archives left in the window.
  const freeArchivesLeft = !isProUser && !isUltraUser && !isPersonnelScanner && archiveAllowance && !archiveAllowance.unlimited && archiveAllowance.remaining > 0
    ? { remaining: archiveAllowance.remaining, limit: archiveAllowance.limit }
    : null;

  // STABLE REFERENCES: Prevent tool-recreation race conditions during SDK runs
  const supabaseRef = useRef(supabaseClient);
  const userRef = useRef(user?.id);
  const currentStoreRef = useRef(currentStore);
  const nearbyStoresRef = useRef(nearbyStores);

  useEffect(() => { supabaseRef.current = supabaseClient; }, [supabaseClient]);
  useEffect(() => { userRef.current = user?.id; }, [user?.id]);
  useEffect(() => { currentStoreRef.current = currentStore; }, [currentStore]);
  useEffect(() => { nearbyStoresRef.current = nearbyStores; }, [nearbyStores]);

  const assistantTools = useMemo(() => {
    return createTools(
      () => supabaseRef.current, 
      () => userRef.current, 
      i18n.language, 
      unitSystem, 
      massUnits, 
      currencySymbol, 
      t, 
      isProUser, 
      isUltraUser
    );
  }, [i18n.language, unitSystem, massUnits, currencySymbol, t, isProUser, isUltraUser]);

  const assistantContextHelpers = useMemo(() => ({
    // The current profile with every message, so an answer never rests on an older lookup in the thread.
    currentProfile: () => {
      const { userProfile, pendingProfilePatch } = useStore.getState();
      return profileSummaryForAssistant(userProfile, pendingProfilePatch);
    },
    currentPage: () => String(location.pathname).substring(0, 50),
    isProUser: () => !!isProUser,
    isUltraUser: () => !!isUltraUser,
    currentStore: () => String(currentStoreRef.current?.name || "None").substring(0, 100),
    nearbyStores: () => nearbyStoresRef.current.slice(0, 3).map(s => s.name).join(", "),
    basketCount: () => activeItems.length,
    available_categories: () => categories.map(c => c.name).join(", "),
    current_date: () => new Date().toLocaleDateString('en-US'),
    pantry_status: () => { try { return JSON.parse(pantrySummary || "{}"); } catch (e) { return {}; } },
    userLocation: () => userCoords.current ? { lat: Number(userCoords.current.lat?.toFixed(4)), lng: Number(userCoords.current.lng?.toFixed(4)) } : "unknown",
    lastUploadedImage: () => liveState.lastThreadImageRef ? `[img:${liveState.lastThreadImageRef.url}]` : "none",
    language: () => i18n.language || "en"
  }), [isProUser, isUltraUser, activeItems.length, categories, pantrySummary, i18n.language]);

  useEffect(() => {
    if (isSignedIn && user?.id && supabaseClient) load(supabaseClient, user.id, false, user);
  }, [isSignedIn, user?.id, supabaseClient, load, user]);

  const categorySaveTimeoutRef = useRef<any>(null);
  useEffect(() => {
    if (!isSignedIn || !user?.id || !supabaseClient || lastCategoriesUpdate === 0) return;
    if (categorySaveTimeoutRef.current) clearTimeout(categorySaveTimeoutRef.current);
    categorySaveTimeoutRef.current = setTimeout(async () => {
      try { await syncCategories(supabaseClient, user.id, categories); } catch (err) { console.error("Failed to auto-save categories:", err); }
    }, 1500);
    return () => { if (categorySaveTimeoutRef.current) clearTimeout(categorySaveTimeoutRef.current); };
  }, [categories, lastCategoriesUpdate, isSignedIn, user?.id, supabaseClient]);

  const settingsSaveTimeoutRef = useRef<any>(null);
  useEffect(() => {
    if (!isSignedIn || !user?.id || !supabaseClient || lastSettingsUpdate === 0) return;
    if (settingsSaveTimeoutRef.current) clearTimeout(settingsSaveTimeoutRef.current);
    settingsSaveTimeoutRef.current = setTimeout(async () => {
      try { const { syncStateToCloud } = useStore.getState(); await syncStateToCloud(supabaseClient, user.id); } catch (err) { console.error("Failed to auto-save settings:", err); }
    }, 2000);
    return () => { if (settingsSaveTimeoutRef.current) clearTimeout(settingsSaveTimeoutRef.current); };
  }, [lastSettingsUpdate, isSignedIn, user?.id, supabaseClient]);

  useEffect(() => {
    let cancelled = false;
    setAddressSuggestions([]);
    setIsSearchingAddress(false);
    if (manualStoreAddress.length <= 3 || manualStoreAddress === lastSelectedAddressRef.current) return;
    const timer = setTimeout(async () => {
      setIsSearchingAddress(true);
      try {
        const suggestions = await autocompleteAddress(manualStoreAddress);
        if (!cancelled) setAddressSuggestions(suggestions);
      } catch (err) {
        console.error("Address error:", err);
      } finally {
        if (!cancelled) setIsSearchingAddress(false);
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [manualStoreAddress]);

  const handleStoreSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!manualStoreSearch.trim()) return;
    try { await searchForStores(manualStoreSearch); } catch (err) { showNotification(t('common.search_failed'), { type: 'error' }); }
  };

  const handleDetectStore = useCallback(async (silent: boolean = false, forceRefresh: boolean = false) => {
    // Blocked vs. slow location need different advice; an approximate (IP)
    // list still says why GPS wasn't used.
    const locationMessage = (code?: number) =>
      code === 1 ? t('merchant.location_denied')
      : code === 3 ? t('merchant.location_timeout')
      : t('merchant.location_error');
    try {
      const result: any = await detectStore({ silent, forceRefresh });
      if (!silent && result?.approximate) {
        showNotification(`${locationMessage(result.reason)} ${t('merchant.location_approximate')}`, { type: 'info', duration: 7000 });
      }
    } catch (err: any) {
      if (!silent) showNotification(locationMessage(err?.code), { type: 'error' });
    }
  }, [detectStore, showNotification, t]);

  useEffect(() => {
    if (!hydrated) return;
    if (isSignedIn && user?.id && !currentStore && nearbyStores.length === 0 && !isDetectingStore && !hasAttemptedDetection.current) {
      hasAttemptedDetection.current = true;
      handleDetectStore(true);
    }
  }, [hydrated, isSignedIn, user?.id, currentStore, nearbyStores.length, isDetectingStore, handleDetectStore]);

  // Lattice Seeding ("Trending Near You"): a persistent local-deals discovery
  // feed, not scoped to cold-start/sparse history — established users want
  // visibility into nearby trending prices too. It's hidden while a FREE
  // user has used up their archives for the 30-day window
  // (isUsageLimitReached), so it can't show through that paywall,
  // but otherwise it's always on regardless of the user's own receipt count.
  const shouldShowLatticeSeed = useMemo(() => {
    if (isPersonnelScanner) return false;
    if (isUsageLimitReached) return false;
    return true;
  }, [isPersonnelScanner, isUsageLimitReached]);

  const [latticeSeedItems, setLatticeSeedItems] = useState<LatticeSeedItem[]>([]);
  const [isLoadingLatticeSeed, setIsLoadingLatticeSeed] = useState(false);
  // Where and how far the list looked, so an item's detail covers the same prices.
  const [latticeSeedScope, setLatticeSeedScope] = useState<LatticeSeedScope | null>(null);
  // Trending's centre, in order: the selected store while it's within the
  // calibration radius of the device (or the device's location is unknown),
  // since that's where the user is shopping; else the device's location, so a
  // store picked days ago elsewhere doesn't skew it; else the saved ZIP; else
  // none (the list then goes national).
  const seedRadiusMiles = resolveCalibration(userProfile?.settings?.intelligence).radiusMiles;
  const seedZip = profileZipCode(userProfile)?.trim() || null;
  const [zipCoords, setZipCoords] = useState<{ zip: string; lat: number; lng: number } | null>(null);
  const seedCentre = useMemo<Pick<LatticeSeedScope, 'lat' | 'lng' | 'source' | 'label'>>(() => {
    const store = currentStore?.coordinates;
    const live = liveCoords ?? userCoords.current;
    const storeNearby = !!store && typeof store.lat === 'number' && typeof store.lng === 'number' &&
      (!live || haversineMiles(live.lat, live.lng, store.lat, store.lng) <= seedRadiusMiles);
    if (storeNearby) return { lat: store!.lat, lng: store!.lng, source: 'store', label: currentStore?.name || null };
    if (live) return { lat: live.lat, lng: live.lng, source: 'location', label: null };
    if (zipCoords && zipCoords.zip === seedZip) return { lat: zipCoords.lat, lng: zipCoords.lng, source: 'zip', label: seedZip };
    return { lat: null, lng: null, source: null, label: null };
  }, [currentStore?.coordinates?.lat, currentStore?.coordinates?.lng, currentStore?.name, liveCoords, seedRadiusMiles, zipCoords, seedZip]);

  // The ZIP is only looked up when nothing better is known.
  const needsZipCentre = !seedCentre.source && !!seedZip;
  useEffect(() => {
    if (!needsZipCentre || !seedZip || zipCoords?.zip === seedZip) return;
    let ignore = false;
    geocodePlace(seedZip).then(coords => {
      if (!ignore && coords) setZipCoords({ zip: seedZip, ...coords });
    });
    return () => { ignore = true; };
  }, [needsZipCentre, seedZip, zipCoords?.zip]);

  useEffect(() => {
    if (!hydrated || !supabaseClient || !shouldShowLatticeSeed) {
      setLatticeSeedItems([]);
      return;
    }
    let ignore = false;
    (async () => {
      setIsLoadingLatticeSeed(true);
      try {
        const { radiusMiles: radius, horizonHours: horizon } = resolveCalibration(userProfile?.settings?.intelligence);
        const { lat, lng } = seedCentre;
        const rows = await getLatticeSeedItems(supabaseClient, lat ?? undefined, lng ?? undefined, radius, horizon, 12);
        if (ignore) return;
        setLatticeSeedScope({ ...seedCentre, radiusMiles: radius, horizonHours: horizon });
        setLatticeSeedItems(rows);
        setIsLoadingLatticeSeed(false);

        // Lattice/receipt photos don't cover every product — backfill any
        // missing image with a Serper stock photo, non-blocking so the grid
        // (name/price/badge) renders immediately and images pop in after.
        const needsImage = rows.filter(r => !r.image_url && r.display_name);
        if (needsImage.length > 0) {
          import('./src/lib/marketDiscovery').then(({ fetchStockImagesById }) =>
            fetchStockImagesById(needsImage.map(r => ({ id: r.slug, name: r.display_name })))
          ).then(byId => {
            if (ignore || Object.keys(byId).length === 0) return;
            setLatticeSeedItems(prev => prev.map(r => byId[r.slug] ? { ...r, image_url: byId[r.slug] } : r));
          }).catch(err => console.error('[LatticeSeed] image enrichment failed:', err));
        }
      } catch (err) {
        console.error('[LatticeSeed] fetch failed:', err);
        if (!ignore) {
          setLatticeSeedItems([]);
          setIsLoadingLatticeSeed(false);
        }
      }
    })();
    return () => { ignore = true; };
  }, [hydrated, supabaseClient, shouldShowLatticeSeed, userProfile?.settings?.intelligence, seedCentre]);

  const [previewSeedItem, setPreviewSeedItem] = useState<LatticeSeedItem | null>(null);

  const handleSeedItemTap = useCallback((item: LatticeSeedItem) => {
    setPreviewSeedItem(item);
  }, []);

  const handleViewSeedItemPrices = useCallback((item: LatticeSeedItem) => {
    if (!item.slug) return;
    setPreviewSeedItem(null);
    navigate(`/prices/${item.slug}/${item.city_slug || 'san-francisco-ca'}`);
  }, [navigate]);

  // Exit flush: whatever differs from the loaded profile (device state plus edits a
  // screen hasn't saved yet) goes out with sendBeacon, which survives the page closing.
  useEffect(() => {
    if (!isSignedIn || !user?.id) return;
    const handleFlush = () => {
      const state = useStore.getState();
      const payload = buildCloudSyncPayload(state);
      if (!payload) return;
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      if (navigator.sendBeacon('/api/sync/flush', blob)) state.markProfilePatched(payload);
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') handleFlush();
    };
    window.addEventListener('beforeunload', handleFlush);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('beforeunload', handleFlush);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isSignedIn, user?.id]);

  // Reset failed grounding status on mount to heal any stuck items
  useEffect(() => {
    if (!activeItems || activeItems.length === 0) return;
    activeItems.forEach(item => {
      if (item.metadata?.groundingStatus === 'failed') {
        updateItem(item.id, {
          metadata: {
            ...item.metadata,
            groundingStatus: undefined
          }
        });
      }
    });
  }, []);

  // BACKGROUND PRODUCT SEARCH GROUNDING TRIGGER
  useEffect(() => {
    if (!activeItems || activeItems.length === 0) return;

    // Find items that need grounding and aren't already loading/failed/completed
    const itemsToGround = activeItems.filter(item => {
      const status = item.metadata?.groundingStatus;
      const hasDeals = !!item.metadata?.groundedDeals;
      return !hasDeals && status !== 'loading' && status !== 'failed' && !groundingInProgress.current.has(item.id);
    });

    itemsToGround.forEach(async (item) => {
      const itemId = item.id;
      groundingInProgress.current.add(itemId);

      // Set status to loading
      updateItem(itemId, {
        metadata: {
          ...(item.metadata || {}),
          groundingStatus: 'loading'
        }
      });

      try {
        let lat = currentStore?.coordinates?.lat ?? null;
        let lng = currentStore?.coordinates?.lng ?? null;
        let resolvedAddress = userLocationAddress;

        if ((lat == null || lng == null || !resolvedAddress) && typeof navigator !== 'undefined' && navigator.geolocation) {
          try {
            const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
            });
            if (lat == null || lng == null) {
              lat = pos.coords.latitude;
              lng = pos.coords.longitude;
            }
            if (!resolvedAddress) {
              resolvedAddress = await reverseGeocodeCoords(pos.coords.latitude, pos.coords.longitude);
              if (resolvedAddress) {
                setUserLocationAddress(resolvedAddress);
              }
            }
          } catch (e) {
            console.warn("[Background Grounding] Could not fetch location dynamically:", e);
          }
        }

        const deals = supabaseClient
          ? await resolveMarketDeals({
              supabase: supabaseClient,
              query: item.name,
              itemPrice: item.price,
              itemSize: item.size,
              itemUnit: item.unit,
              userLat: lat,
              userLng: lng,
              userAddress: resolvedAddress,
            })
          : [];

        updateItem(itemId, {
          metadata: {
            ...(item.metadata || {}),
            groundingStatus: 'success',
            groundedDeals: deals || [],
            userLocationAddress: resolvedAddress || null
          }
        });
      } catch (err) {
        console.error(`[Background Grounding] Failed for item ${itemId}:`, err);
        updateItem(itemId, {
          metadata: {
            ...(item.metadata || {}),
            groundingStatus: 'failed'
          }
        });
      } finally {
        groundingInProgress.current.delete(itemId);
      }
    });
  }, [activeItems, currentStore, updateItem, userLocationAddress, supabaseClient]);

  const handleManualStoreSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualStoreName.trim() || !manualStoreAddress.trim()) return;
    setIsGeocodingStore(true);
    try {
      const pick = selectedPlaceRef.current;
      const placeId = pick && pick.name === manualStoreName.trim() && pick.address === manualStoreAddress.trim() ? pick.placeId : undefined;
      const geo = await geocodeStore(manualStoreName.trim(), manualStoreAddress.trim(), placeId);
      await selectStore({ name: manualStoreName.trim(), address: (placeId && geo?.address) || manualStoreAddress.trim() || t('common.unknown_address'), coordinates: geo ? { lat: geo.lat, lng: geo.lng } : undefined, url: geo?.url });
      setShowStoreModal(false); setManualStoreName(''); setManualStoreAddress('');
      lastSelectedAddressRef.current = null; // Reset for next time
    } catch (err) {
      await selectStore({ name: manualStoreName.trim(), address: manualStoreAddress.trim() || t('common.unknown_address') });
      setShowStoreModal(false);
      lastSelectedAddressRef.current = null;
    } finally { setIsGeocodingStore(false); }
  };

  const onAddressSelect = (suggestion: { name: string; address: string; placeId?: string }) => {
    lastSelectedAddressRef.current = suggestion.address;
    selectedPlaceRef.current = { placeId: suggestion.placeId, name: suggestion.name.trim(), address: suggestion.address.trim() };
    setManualStoreAddress(suggestion.address);
    setManualStoreName(suggestion.name);
    setAddressSuggestions([]);
  };  const hasAttemptedDetection = useRef(false);

  const handleCloseAddModal = async () => {
    if (newItemImage?.startsWith('local:')) {
      const { ImageCacheService } = await import('./services/imageCacheService');
      await ImageCacheService.removeImage(newItemImage);
    }
    setShowAddModal(false);
    // A photo scan still running must not refill the form after it's closed.
    manualExtractionRef.current = null;
    setIsExtractingManual(false);
    setBrand(null); setProduct(''); setVariety(null); setSize(null); setUnit(null);
    setNewItemPrice(''); setNewItemCategory('Other'); setNewItemUPC(null); setNewItemGTIN(null);
    setNewItemPLU(null); setNewItemMetadata(null); setNewItemImage(null); setNewItemQuantity(1);
    setNewItemCanonicalId(''); setNewItemShelfLife(null); setNewItemExpirationDate(null);
    setNewItemEmbedding(null);
    setManualAddCatSearch('');
  };

  // The photo extraction feeding the Add Product form, so its background
  // identifier lookup lands on the right form or basket item (see
  // handleManualImageExtraction).
  const manualExtractionRef = useRef<{ token: number; addedId?: string } | null>(null);

  const handleAddToBasket = async (e: React.FormEvent) => {
    if (e) e.preventDefault();
    const name = formatDisplayName({ brand, product, variety, size, unit });
    const slug = newItemCanonicalId || constructProtocolSlug({ brand, product, variety, size, unit });
    // Size is typed as text ("500" or "XL"); store numbers as numbers.
    const sizeValue = typeof size === 'string' && /^\d+(\.\d+)?$/.test(size.trim()) ? parseFloat(size) : size;
    const addedId = addItem(name, Math.max(0, parseFloat(newItemPrice) || 0), newItemCategory, newItemImage || undefined, newItemQuantity, slug, unit || undefined, brand, product, variety, sizeValue, newItemShelfLife, newItemEmbedding, newItemPLU, newItemMetadata, newItemUPC, newItemGTIN);
    if (manualExtractionRef.current && !manualExtractionRef.current.addedId) manualExtractionRef.current.addedId = addedId;
    setShowAddModal(false); setBrand(null); setProduct(''); setVariety(null); setSize(null); setUnit(null); setNewItemPrice(''); setNewItemCategory('Other'); setNewItemUPC(null); setNewItemGTIN(null); setNewItemPLU(null); setNewItemMetadata(null); setNewItemImage(null); setNewItemQuantity(1); setNewItemCanonicalId(''); setNewItemShelfLife(null); setNewItemExpirationDate(null); setNewItemEmbedding(null); setManualAddCatSearch('');

    // No embedding yet (typed by hand, or the photo's is still on its way):
    // make one in the background instead of holding up the add.
    if (!newItemEmbedding && name && addedId) {
      (async () => {
        try {
          const { generateEmbedding } = await import('./services/geminiService');
          const emb = await generateEmbedding(name, undefined);
          if (!emb || emb.length === 0) return;
          const { items: liveItems, latticeItems: liveLatticeItems, operatingContext, updateItem: updateBasketItem } = useStore.getState();
          const live = (operatingContext === 'personnel' ? liveLatticeItems : liveItems).find(i => i.id === addedId);
          if (live && !live.embedding) updateBasketItem(addedId, { embedding: emb });
        } catch (err) { console.error("Embedding fail:", err); }
      })();
    }
  };

  /**
   * Scans one photo. Resolves as soon as its items are in the basket (or the
   * scan ended); the identifier lookups that follow keep running in the
   * background. `queuedPendingId`: the "analyzing" card a batch import already
   * shows for this photo (and counted in processingCount), so the indicator
   * never drops out between photos.
   */
  const handleCapture = (sourceBase64: string, sourceBlob?: Blob | File, modeOverride?: ScanMode, queuedPendingId?: string): Promise<void> => {
    const activeMode = modeOverride || scanMode;
    setIsCameraOpen(false);
    if (!queuedPendingId) setProcessingCount(prev => prev + 1);
    let localImageUrl: string | null = null;
    // The scan stops blocking the basket as soon as its items are shown; the
    // identifier lookups that follow run in the background.
    let released = false;
    let pendingId: string | null = queuedPendingId || null;
    let onShown: () => void = () => {};
    const shown = new Promise<void>(resolve => { onShown = resolve; });
    const release = () => {
      if (released) return;
      released = true;
      if (pendingId) useStore.getState().removePendingItem(pendingId);
      setProcessingCount(prev => Math.max(0, prev - 1));
      onShown();
    };

    const work = (async () => {
      try {
        const { processImage } = await import('./services/imageUtils');
        const { base64, blob } = await processImage(sourceBlob || `data:image/jpeg;base64,${sourceBase64}`, 1200);
        const { ImageCacheService } = await import('./services/imageCacheService');
        localImageUrl = await ImageCacheService.saveImage(blob);
        if (pendingId) {
          // The batch's placeholder card now shows this photo.
          const imageUrl = localImageUrl;
          useStore.setState(state => ({ pendingItems: state.pendingItems.map(p => p.id === pendingId ? { ...p, imageUrl } : p) }));
        } else {
          pendingId = `pending_${Date.now()}`;
          // cast to any to handle 'import' mode which is a UI-only designation for capture origin
          useStore.getState().addPendingItem(pendingId, activeMode as any, localImageUrl);
        }

        const dictionary = await (await import('./services/geminiService')).getProductDictionary(supabaseClient!, user!.id);

        // Use 'receipt' mode for camera receipt scans and file imports, so store/date metadata
        // is extracted from imported receipts. Stage 4 embeddings fire based on isReceipt, not mode.
        const extractionMode = (activeMode === 'receipt' || activeMode === 'import') ? 'receipt' : 'tag';

        // Basket item ids by the index of the item in the scan.
        const basketIds: Array<string | undefined> = [];
        let receiptMetadata: Promise<void> | null = null;

        const result = await extractItemsFromImage(
          supabaseClient!, base64, categories, extractionMode as any, userProfile?.ai_profile || undefined, dictionary, unitSystem, massUnits, i18n.language,
          // Grounded (background): add the identifiers and embedding to the item already in the basket.
          (item, index) => {
            const id = basketIds[index];
            if (!id) return;
            const { items: liveItems, latticeItems: liveLatticeItems, operatingContext, updateItem: updateBasketItem } = useStore.getState();
            const live = (operatingContext === 'personnel' ? liveLatticeItems : liveItems).find(b => b.id === id);
            if (!live) return; // removed from the basket meanwhile
            updateBasketItem(id, {
              upc: live.upc || item.upc || undefined,
              gtin: live.gtin || item.gtin || undefined,
              plu_code: live.plu_code || item.plu_code || undefined,
              embedding: live.embedding || item.embedding || undefined,
              metadata: { ...(item.metadata || {}), ...(live.metadata || {}) },
            });
          },
          activeMode !== 'import',
          false,
          // Drafted: show the items now.
          (drafted, draft) => {
            drafted.forEach((item, index) => {
              basketIds[index] = addItem(item.name || item.product || 'Unknown Item', item.price, item.category, localImageUrl!, item.quantity || 1, item.canonicalId, item.unit || undefined, item.brand, item.product, item.variety, item.size, item.shelfLife, item.embedding, item.plu_code, item.metadata, item.upc, item.gtin);
            });
            release();
            if (drafted.length && draft.isReceipt && (activeMode === 'receipt' || activeMode === 'import') && !isLatticeMode && user) recordBlogStage('first_scan', user.id, user.createdAt);
            if (drafted.length) showNotification(t('app.items_extracted', { count: drafted.length }), { type: 'success', duration: 3000 });

            // Only apply store metadata if Gemini confirms this is an actual receipt
            if (draft.isReceipt) {
              setReceiptImageUrl(localImageUrl);
              receiptMetadata = applyReceiptMetadata(draft);
            }

            // Best-effort: attach a clean stock/product photo per item, on top of localImageUrl
            // (the actual scan, kept as proof-of-purchase). Never blocks or fails the scan itself.
            if (drafted.length) {
              (async () => {
                try {
                  const { fetchProductImages } = await import('./src/lib/marketDiscovery');
                  const images = await fetchProductImages(drafted.map((i: any) => ({ name: i.name, brand: i.brand })));
                  const { items: liveItems, latticeItems: liveLatticeItems, operatingContext, updateItem: updateBasketItem } = useStore.getState();
                  const basket = operatingContext === 'personnel' ? liveLatticeItems : liveItems;
                  drafted.forEach((extracted: any, index: number) => {
                    const url = images[extracted.name];
                    const match = url && basket.find(b => b.id === basketIds[index]);
                    if (match) updateBasketItem(match.id, { metadata: { ...(match.metadata || {}), stockImageUrl: url } });
                  });
                } catch (imgErr) {
                  console.warn('[handleCapture] stock image lookup failed (non-fatal):', imgErr);
                }
              })();
            }
          },
        );

        // Content Verification Logic (a mismatch stops before any item is drafted)
        if (activeMode === 'receipt' && !result.isReceipt) {
          showNotification(t('app.not_a_receipt'), { type: 'error' });
          // Delete from local cache since it's invalid
          await ImageCacheService.removeImage(localImageUrl);
          return;
        }

        if (activeMode === 'tag' && result.isReceipt) {
          showNotification(t('app.not_a_tag'), { type: 'error' });
          await ImageCacheService.removeImage(localImageUrl);
          return;
        }

        // A receipt whose items couldn't be read still sets the store and date.
        if (!receiptMetadata && result.isReceipt) {
          setReceiptImageUrl(localImageUrl);
          receiptMetadata = applyReceiptMetadata(result);
        }
        if (receiptMetadata) await receiptMetadata;
      } catch (err: any) {
        console.error("Extraction error:", err);
        showNotification(t(getGeminiErrorKey(err)), { type: 'error', duration: 6000 });
        // CRITICAL: Ensure image is cleaned up if we failed before adding it to a history entry, 
        // otherwise it sits in IndexedDB forever if the user doesn't manually clear.
        // (Items already in the basket keep it.)
        if (localImageUrl && !released) {
          try {
            const { ImageCacheService } = await import('./services/imageCacheService');
            await ImageCacheService.removeImage(localImageUrl);
          } catch (e) { /* silent fail on cleanup */ }
        }
      } finally { release(); }
    })();

    return Promise.race([shown, work]);
  };

  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    const mode = scanMode;
    // Every photo gets its "analyzing" card up front, and the next photo
    // starts as soon as the previous one's items are shown.
    const queued = files.map((_, i) => `pending_${Date.now()}_${i}`);
    setProcessingCount(prev => prev + files.length);
    const { addPendingItem } = useStore.getState();
    queued.forEach(id => addPendingItem(id, mode as any));
    for (let i = 0; i < files.length; i++) await handleCapture("", files[i], mode, queued[i]);
  };

  const handleManualImageExtraction = async (sourceBase64: string) => {
    // Image is already saved to IndexedDB and set on state by AddProductModal.handleImageChange —
    // do NOT re-process or re-save here. Just run extraction and repopulate form fields.
    setIsExtractingManual(true);
    // Clear stale fields and embedding so the modal resets cleanly before repopulating
    setBrand(null); setProduct(''); setVariety(null); setSize(null); setUnit(null);
    setNewItemUPC(null); setNewItemGTIN(null); setNewItemPLU(null); setNewItemMetadata(null);
    setNewItemPrice(''); setNewItemCategory('Other'); setNewItemCanonicalId(''); setNewItemEmbedding(null);
    setNewItemShelfLife(null); setNewItemExpirationDate(null);
    const token = Date.now();
    manualExtractionRef.current = { token };
    try {
      const result = await extractItemsFromImage(
        supabaseClient!, sourceBase64, categories, 'tag', userProfile?.ai_profile || undefined, [], unitSystem, massUnits, i18n.language,
        // Grounded (background): identifiers and embedding go to the form, or
        // to the basket item if it was already added. A newer photo wins.
        (grounded, index) => {
          const current = manualExtractionRef.current;
          if (index !== 0 || current?.token !== token) return;
          if (current.addedId) {
            const { items: liveItems, latticeItems: liveLatticeItems, operatingContext, updateItem: updateBasketItem } = useStore.getState();
            const live = (operatingContext === 'personnel' ? liveLatticeItems : liveItems).find(i => i.id === current.addedId);
            if (live) updateBasketItem(live.id, {
              upc: live.upc || grounded.upc || undefined,
              gtin: live.gtin || grounded.gtin || undefined,
              plu_code: live.plu_code || grounded.plu_code || undefined,
              embedding: live.embedding || grounded.embedding || undefined,
              metadata: { ...(grounded.metadata || {}), ...(live.metadata || {}) },
            });
            return;
          }
          setNewItemUPC(prev => prev || grounded.upc || null);
          setNewItemGTIN(prev => prev || grounded.gtin || null);
          setNewItemPLU(prev => prev || grounded.plu_code || null);
          setNewItemEmbedding(prev => prev || grounded.embedding || null);
          setNewItemMetadata((prev: any) => ({ ...(grounded.metadata || {}), ...(prev || {}) }));
        },
        true,
        false,
        // Drafted: fill the form now; the user can review and add right away.
        (drafted) => {
          if (manualExtractionRef.current?.token !== token) return;
          const item: any = drafted[0];
          setBrand(item.brand || null); setProduct(item.product || ''); setVariety(item.variety || null);
          setNewItemUPC(item.upc || null); setNewItemGTIN(item.gtin || null); setNewItemPLU(item.plu_code || null); setNewItemMetadata(item.metadata || null);
          let rSize: any = item.size || null, rUnit: any = item.unit || null;
          if (typeof item.size === 'string' && !item.unit) {
            const m = item.size.match(/^([\d.]+)([a-zA-Z\s]+)$/);
            if (m) { rSize = parseFloat(m[1]); rUnit = m[2].trim().toLowerCase(); }
          }
          setSize(rSize); setUnit(rUnit); setNewItemPrice(String(item.price)); setNewItemCategory(item.category);
          setNewItemCanonicalId((item as any).canonicalId || ''); setNewItemEmbedding(item.embedding || null);

          // Best-effort stock photo — the uploaded photo (newItemImage) stays as the actual proof shot.
          const lookupName = (item as any).name || item.product;
          if (lookupName) {
            import('./src/lib/marketDiscovery').then(({ fetchProductImages }) =>
              fetchProductImages([{ name: lookupName, brand: item.brand }])
            ).then((images) => {
              const url = images[lookupName];
              if (url) setNewItemMetadata((prev: any) => ({ ...(prev || {}), stockImageUrl: url }));
            }).catch((imgErr) => console.warn('[handleManualImageExtraction] stock image lookup failed (non-fatal):', imgErr));
          }
          setNewItemShelfLife(item.shelfLife || null);
          if (item.shelfLife) {
            const d = new Date(tripDateTime); d.setDate(d.getDate() + item.shelfLife);
            setNewItemExpirationDate(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
          }
          setIsExtractingManual(false);
        },
      );

      if (result.isReceipt) {
        showNotification(t('app.manual_item_required'), { type: 'error' });
        // Clean up the image that handleImageChange already saved
        if (newItemImage?.startsWith('local:')) {
          const { ImageCacheService } = await import('./services/imageCacheService');
          await ImageCacheService.removeImage(newItemImage);
        }
        setNewItemImage(null);
        return;
      }
    } catch (err: any) {
      console.error("Manual extraction error:", err);
      showNotification(t(getGeminiErrorKey(err)), { type: 'error', duration: 5000 });
      showNotification(t('app.manual_entry_prompt', 'Please enter the item details manually.'), { type: 'info', duration: 4000 });
    } finally {
      setIsExtractingManual(false);
    }
  };

  const saveToHistory = async (forceLattice?: boolean) => {
    if (archiveInFlight.current || !currentStore || activeItems.length === 0 || !supabaseClient || !user?.id) return;
    archiveInFlight.current = true;
    const isLattice = forceLattice ?? (isLatticeMode || latticeParticipation);
    setIsHardSyncing(true);
    try {
      const { ImageCacheService } = await import('./services/imageCacheService');
      // Scanned receipts and checkout attachments share the same physical receipt field.
      // Upload first; a failure keeps the basket and selected photo ready to retry.
      let archUrl: string | null;
      try { archUrl = await uploadBasketReceipt(supabaseClient, user.id, receiptImageUrl); }
      catch { throw new ReceiptAttachmentError('save_failed'); }
      if (archUrl !== receiptImageUrl) setReceiptImageUrl(archUrl);
      // Deduplicate: each unique local blob is uploaded exactly once.
      // Seed with the receipt upload so items sharing that URL reuse it.
      const resolvedUrls = new Map<string, string | null>();
      if (receiptImageUrl && archUrl && archUrl !== receiptImageUrl) resolvedUrls.set(receiptImageUrl, archUrl);
      const uniqueLocalUrls = [...new Set(activeItems.map(i => i.imageUrl).filter((url): url is string => !!url && (url.startsWith('local:') || url.startsWith('chat:') || url.startsWith('data:'))))].filter(url => !resolvedUrls.has(url));
      await Promise.all(uniqueLocalUrls.map(async (localUrl) => {
        try {
          const b = (localUrl.startsWith('local:') || localUrl.startsWith('chat:')) ? await ImageCacheService.getImageBlob(localUrl) : dataURLToBlob(localUrl);
          if (b) { const u = await uploadItemImage(supabaseClient, user.id, b, 'item.jpg'); await ImageCacheService.registerCloudUrl(localUrl, u); resolvedUrls.set(localUrl, u); }
          else { console.warn(`[saveToHistory] Blob missing from IndexedDB for ${localUrl} — dropping image reference`); resolvedUrls.set(localUrl, null); }
        } catch (e) { console.warn(`[saveToHistory] Image upload failed for ${localUrl} — dropping image reference:`, e); resolvedUrls.set(localUrl, null); }
      }));
      const archItems = activeItems.map(i => (i.imageUrl && resolvedUrls.has(i.imageUrl)) ? { ...i, imageUrl: resolvedUrls.get(i.imageUrl) } : i);
      const { itemsWithLattice, confidenceScore: confidence, leakageAmount: leakage, savingsAmount: savings } = computeTripTotals(archItems as any);
      // Only admin Lattice batches (isLatticeMode) have no trip budget. isLattice is also true for
      // ordinary trips when the user shares prices with the Lattice, so it can't be used here.
      const archivedBudget = isLatticeMode ? null : useStore.getState().tripBudget;
      const entry: ShoppingHistoryEntry = { id: Math.random().toString(36).substring(7), date: tripDateTime.toISOString(), totalCost: totalBasket, leakageAmount: leakage, savingsAmount: savings, confidenceScore: confidence, items: archItems, storeInfo: currentStore, receiptImageUrl: archUrl, budgetAmount: archivedBudget } as any;
      const result = await saveHistoryEntry(supabaseClient, user.id, entry, categories, isLattice);
      setShowArchiveModal(false);
      if (result.id && !isLatticeMode) recordBlogStage('first_archive', user.id, user.createdAt);
      if (itemsWithLattice > 0 && result.id) {
        // Non-blocking: a failed analytics write must never undo a saved trip.
        recordUsefulVerdict(supabaseClient, user.id, result.id, itemsWithLattice).catch(
          error => console.warn('[Acquisition] Verdict tracking failed:', error)
        );
      }
      
      // Optimistic update of analytics state
      if (analytics) {
        setAnalytics({
          ...analytics,
          lifetime_total: (analytics.lifetime_total || 0) + totalBasket,
          lifetime_leakage: (analytics.lifetime_leakage || 0) + leakage,
          lifetime_savings: (analytics.lifetime_savings || 0) + savings,
          trip_count: (analytics.trip_count || 0) + 1,
          avg_trip: ((analytics.lifetime_total || 0) + totalBasket) / ((analytics.trip_count || 0) + 1)
        });
      }

      setHistory(prev => [result, ...prev]); clearBasket(); setReceiptDatePending(false); setTripDateTime(new Date()); setIsTimeManual(false); setReceiptImageUrl(null);
      await ImageCacheService.clearAll(); await load(supabaseClient, user.id, false);
      refreshArchiveAllowance();
      useStore.getState().resetTripBudget();
      const r = (result as any).reward || 0;
      const archivedBudgetState = getBudgetState(archivedBudget, totalBasket);
      if (archivedBudgetState) {
        // Trip Budget: the archive message says how the trip did against its budget.
        if (archivedBudgetState.status === 'over') showNotification(t('budget.archived_over', { amount: formatBudgetMoney(-archivedBudgetState.remaining) }), { type: 'warning' });
        else if (archivedBudgetState.remaining > 0) showNotification(t('budget.archived_under', { amount: formatBudgetMoney(archivedBudgetState.remaining) }), { type: 'success' });
        else showNotification(t('budget.archived_within'), { type: 'success' });
      }
      else if (r > 0) showNotification(t('app.trip_saved_reward', { count: r }), { type: 'success' });
      else showNotification(t('app.trip_saved'), { type: 'success' });
    } catch (err: any) {
      if (err instanceof ReceiptAttachmentError) {
        showNotification(t('receipt_attachment.archive_failed', { defaultValue: 'The receipt could not be uploaded. Your basket is still here. Retry or remove the attachment before archiving.' }), { type: 'error' });
      } else if (String(err?.message || '').includes('archive_limit_reached')) {
        // The free plan's allowance ran out (e.g. archived from another device).
        showNotification(t('app.free_archive_limit_toast', { limit: archiveAllowance?.limit ?? 3, days: archiveAllowance?.windowDays ?? 30, defaultValue: "You've archived {{limit}} trips in the last {{days}} days on the free plan." }), { type: 'error' });
        refreshArchiveAllowance();
      } else {
        showNotification(t('app.save_error'), { type: 'error' });
      }
    } finally { archiveInFlight.current = false; setIsHardSyncing(false); }
  };

  useEffect(() => { liveState.saveToHistory = saveToHistory; }, [saveToHistory]);

  const [showArchiveModal, setShowArchiveModal] = useState(false);
  useEffect(() => { liveState.triggerArchiveModal = () => setShowArchiveModal(true); }, []);

  const handleClearHistory = async () => {
    try {
      for (const e of historyRaw) await deleteHistoryEntry(supabaseClient!, user!.id, e.id);
      setHistory([]); 
      
      // Clear analytics optimistically
      if (analytics) {
        setAnalytics({
          ...analytics,
          lifetime_total: 0,
          lifetime_leakage: 0,
          lifetime_savings: 0,
          trip_count: 0,
          avg_trip: 0,
          category_spend: {},
          category_distribution: []
        });
      }

      showNotification(t('history.wipe_success'), { type: 'success' });
      if (supabaseClient && user?.id) await load(supabaseClient, user.id, false);
    } catch (err) { showNotification(t('history.wipe_failed'), { type: 'error' }); }
  };

  const handleHistoryBoundsChange = (bounds: any) => {
    console.log("[History] Bounds changed:", bounds);
  };

  const handleDeleteHistoryEntry = async (id: string, options?: { eraseImages?: boolean }) => {
    try {
      const entryToDelete = historyRaw.find(h => h.id === id);
      await deleteHistoryEntry(supabaseClient!, user!.id, id, options);
      setHistory(historyRaw.filter(h => h.id !== id)); 
      
      // Optimistic update of analytics state on deletion
      if (analytics && entryToDelete) {
        const nextTotal = Math.max(0, (analytics.lifetime_total || 0) - (entryToDelete.totalCost || 0));
        const nextLeakage = Math.max(0, (analytics.lifetime_leakage || 0) - ((entryToDelete as any).leakageAmount || 0));
        const nextSavings = Math.max(0, (analytics.lifetime_savings || 0) - ((entryToDelete as any).savingsAmount || 0));
        const nextTripCount = Math.max(0, (analytics.trip_count || 0) - 1);
        setAnalytics({
          ...analytics,
          lifetime_total: nextTotal,
          lifetime_leakage: nextLeakage,
          lifetime_savings: nextSavings,
          trip_count: nextTripCount,
          avg_trip: nextTripCount > 0 ? nextTotal / nextTripCount : 0
        });
      }

      showNotification(t('history.delete_success'), { type: 'success' });
      if (supabaseClient && user?.id) await load(supabaseClient, user.id, false);
    } catch (err) { showNotification(t('history.delete_failed'), { type: 'error' }); }
  };

  const handleSaveInsight = async (data: any) => {
    if (!supabaseClient || !user?.id) return;
    try {
      const now = new Date().toISOString();
      const updated = { ...data, generatedAt: now };
      await (await import('./services/supabaseService')).saveInsight(supabaseClient, user.id, 'master_insight', updated);
      setMasterInsight(updated);
      if (userProfile) setUserProfile({ ...userProfile, last_insight_at: now });
    } catch (err) { console.error("Save insight fail:", err); }
  };

  const handleAddSuggested = (item: any) => {
    addItem(item.name || item.canonicalName, item.bestPrice || item.price || 0, item.category || 'Other', item.imageUrl, 1, item.canonicalId, item.unit, item.brand, item.product, item.variety, item.size, item.shelfLife, item.embedding, item.plu_code, item.metadata, item.upc, item.gtin);
    showNotification(t('dashboard.item_added', { name: item.name || item.canonicalName }), { type: 'success', duration: 3000 });
  };

  // Hide and snooze live in profiles.settings.restock (patch_profile deep-merges it).
  const patchRestockSettings = async (patch: RestockSettings) => {
    if (!supabaseClient || !user?.id || !userProfile) return;
    const previous = userProfile;
    const current = userProfile.settings?.restock || {};
    const restock = { hidden: { ...current.hidden, ...patch.hidden }, snoozed: { ...current.snoozed, ...patch.snoozed } };
    setUserProfile({ ...userProfile, settings: { ...userProfile.settings, restock } } as UserProfile);
    try {
      await updateProfile(supabaseClient, user.id, { settings: { restock: patch } } as any);
    } catch (err) {
      console.error("Restock preference save failed:", err);
      setUserProfile(previous);
      showNotification(t('settings.save_failed'), { type: 'error' });
    }
  };

  const handleSnoozeRestock = (item: RestockItem) => {
    const until = new Date(Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000).toISOString();
    // Family key plus every product's key, so the snooze holds if the family grouping changes.
    patchRestockSettings({ snoozed: Object.fromEntries(restockSettingKeys(item).map(k => [k, until])) });
    showNotification(t('restock.snoozed_toast', { defaultValue: 'Snoozed {{name}} for a week', name: item.name }), { type: 'success', duration: 3000 });
  };

  const handleHideRestock = (item: RestockItem) => {
    patchRestockSettings({ hidden: Object.fromEntries(restockSettingKeys(item).map(k => [k, true])) });
    showNotification(t('restock.hidden_toast', { defaultValue: "We won't suggest {{name}} again", name: item.name }), { type: 'success', duration: 3000 });
  };

  const [isGeneratingTips, setIsGeneratingTips] = useState(false);
  const pendingProfilePatch = useStore(state => state.pendingProfilePatch);
  const tipsProfile = mergePersonalization(userProfile?.ai_profile || {}, pendingProfilePatch?.ai_profile || {});
  const currentTipsProfileSignature = profileSignature(tipsProfile);
  const savedTipsProfileSignature = useStore(state => state.basketInsightProfileSignature);
  const tipsProfileChanged = savedTipsProfileSignature !== currentTipsProfileSignature;
  const handleUpdateBasketInsights = async () => {
    if (!supabaseClient || !user?.id || activeItems.length === 0) return;
    setIsGeneratingTips(true);
    const generationStarted = Date.now();
    try {
      const signature = currentTipsProfileSignature;
      const tips = await generateBasketInsight(activeItems, sortedHistory.slice(0, 10), tipsProfile, i18n.language);
      const latest = useStore.getState();
      if (latest.userProfile?.id !== user.id || profileSignature(mergePersonalization(latest.userProfile?.ai_profile || {}, latest.pendingProfilePatch?.ai_profile || {})) !== signature) return;
      await (await import('./services/supabaseService')).saveInsight(supabaseClient, user.id, 'basket_tips', { tips, profileSignature: signature });
      setBasketInsight(tips, signature);
      setLastInsightGeneratedTimestamp(generationStarted);
    } catch (err) { showNotification(t(getGeminiErrorKey(err)), { type: 'error', duration: 6000 }); } finally { setIsGeneratingTips(false); }
  };

  const handleLanguageChange = (code: string) => { setLanguage(code); i18n.changeLanguage(code); };
  const handleCurrencyChange = (code: string) => { setCurrency(code); };

  const handleItemImageUpload = useCallback(async (id: string, file: File | string) => {
    if (!supabaseClient || !user?.id) return;
    try {
      let imageUrl = ''; let emb: number[] | null = null;
      if (typeof file === 'string') imageUrl = file;
      else {
        const { processImage } = await import('./services/imageUtils');
        const { base64, blob } = await processImage(file, 800);
        const { ImageCacheService } = await import('./services/imageCacheService');
        const item = activeItems.find(i => i.id === id);
        // Only delete the old local image if no other basket item still references it
        if (item?.imageUrl?.startsWith('local:')) {
          const stillUsed = activeItems.some(i => i.id !== id && i.imageUrl === item.imageUrl);
          if (!stillUsed) await ImageCacheService.removeImage(item.imageUrl);
        }
        imageUrl = await ImageCacheService.saveImage(blob);
        if (item) {
          const { generateEmbedding } = await import('./services/geminiService');
          emb = await generateEmbedding(item.canonicalId || item.name, base64);
        }
      }
      updateItem(id, { imageUrl, ...(emb ? { embedding: emb } : {}) });
      showNotification(t('app.image_updated'), { type: 'success' });
    } catch (err) { showNotification(t('app.upload_error'), { type: 'error' }); }
  }, [supabaseClient, user?.id, updateItem, activeItems, t]);

  const handleBypassCooldownAsync = async (type: 'audit' | 'tips') => { handleBypassCooldown(type); return true; };

  // Rendered bare (no banners or app chrome) and without waiting for auth in dev, so the recorder sees only the tour.
  if (location.pathname === TOUR_PATH || location.pathname === AISLE_PATH) {
    if (import.meta.env.DEV || isAdmin) return <Suspense fallback={null}>{location.pathname === AISLE_PATH ? <AisleTourView /> : <ProductTourView />}</Suspense>;
    if (isLoaded && (!isSignedIn || userProfile)) return <Navigate to="/" replace />;
  }
  if (!isLoaded || isCinematicLoading) return <CinematicLoader progress={appLoadingProgress} isUpdate={needRefresh} />;
  if (isLoaded && isSignedIn && !userProfile) return <CinematicLoader progress={50} />;

  if (isSignedIn && userProfile && !isAdmin && isRestrictedWorkspaceRoute(location.pathname, location.search)) {
    return <Navigate to="/home" replace />;
  }

  const getDashboardPath = () => {
    if (!userProfile) return '/home';
    if (userProfile.role === 'admin') return '/home/admin';
    return '/home/dashboard';
  };

  if (isSignedIn && userProfile && userProfile.has_onboarded === false) {
    return <OnboardingView userProfile={userProfile} supabaseClient={supabaseClient!} onComplete={(p) => { setUserProfile(p); navigate('/home'); }} />;
  }

  const isPublicRoute = PUBLIC_PATHS.includes(location.pathname) || PUBLIC_DYNAMIC_PREFIXES.some(prefix => location.pathname.startsWith(prefix));

  return (
    <GlobalErrorBoundary>
      <SupabaseContext.Provider value={supabaseClient}>
        <div className={`flex flex-col relative ${isPublicRoute ? 'overflow-x-clip bg-[#0A0F1E] min-h-screen' : 'overflow-x-hidden bg-[#f8fafc] h-[100dvh] overflow-hidden'}`}>
          <StagingRedirectModal />
          <PrivacyBanner />
          {/* Rendered here (top-level, always mounted) rather than nested inside the
              /home/* route branch — it was getting visually trapped behind later-
              rendered modals (BulkImportView and others) despite its own z-[500],
              because it sat several ancestors deep in a different stacking branch. */}
          <Suspense fallback={null}>
            <NotificationSystem notification={notification} onClose={() => setNotification(null)} />
          </Suspense>
          {/* Clerk mounts and starts loading its script on every route (not just
              auth pages), so a load failure (usually an ad-blocker/privacy
              extension blocking clerk.cartlens.co) previously surfaced only as an
              unhandled promise rejection in Sentry with no user-facing feedback —
              it's rejected async, so no ErrorBoundary can catch it. This banner
              gives visitors on any page a visible, actionable notice instead. */}
          <ClerkFailed>
            <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] max-w-md w-[calc(100%-2rem)] flex items-center gap-3 rounded-2xl border border-amber-500/30 bg-[#0d1526] px-5 py-4 shadow-2xl">
              <span className="text-xs font-semibold text-white/70 leading-relaxed flex-1">
                {t('auth.clerk_load_failed_banner', {
                  defaultValue: "Some account features couldn't load — an ad-blocker or privacy extension may be blocking our authentication provider. Try reloading or disabling it for this site.",
                })}
              </span>
              <button
                onClick={() => window.location.reload()}
                className="shrink-0 text-[10px] font-black uppercase tracking-widest text-[#10B981] hover:text-[#34D399] transition-colors"
              >
                {t('common.reload', { defaultValue: 'Reload' })}
              </button>
            </div>
          </ClerkFailed>
          <input type="file" ref={fileInputRef} onChange={handleFileImport} className="hidden" accept="image/*" multiple />

          {location.pathname === DEMOS_PATH ? (
            // Unlisted animation previews: admins only, except in local dev.
            (isAdmin || import.meta.env.DEV)
              ? <Suspense fallback={<CinematicLoader />}><DemosGalleryView /></Suspense>
              : <Navigate to="/" replace />
          ) : location.pathname === '/deck' ? <PitchDeck setShowAuthModal={handleAuthTrigger} /> : isPublicRoute ? (
            <div className="flex-1 flex flex-col w-full">
              <Suspense fallback={<CinematicLoader />}>
                <Routes>
                  <Route path="/" element={isSignedIn ? <Navigate to={getDashboardPath()} replace /> : <LandingPage setShowAuthModal={handleAuthTrigger} />} />
                  <Route path="/privacy" element={<LegalView setShowAuthModal={handleAuthTrigger} />} />
                  <Route path="/terms" element={<LegalView setShowAuthModal={handleAuthTrigger} />} />
                  <Route path="/brand" element={<BrandView setShowAuthModal={handleAuthTrigger} />} />
                  <Route path="/about" element={<AboutView setShowAuthModal={handleAuthTrigger} />} />
                  <Route path="/plu" element={<ErrorBoundary><CommoditiesView /></ErrorBoundary>} />
                  <Route path="/units" element={<ErrorBoundary><UnitsView /></ErrorBoundary>} />
                  <Route path="/faq" element={<FAQView setShowAuthModal={handleAuthTrigger} />} />
                  <Route path="/pricing" element={<ErrorBoundary><PricingView setShowAuthModal={handleAuthTrigger} /></ErrorBoundary>} />
                  <Route path="/features" element={<ErrorBoundary><FeaturesHubView setShowAuthModal={handleAuthTrigger} /></ErrorBoundary>} />
                  <Route path="/features/:slug" element={<ErrorBoundary><FeaturePageView setShowAuthModal={handleAuthTrigger} /></ErrorBoundary>} />
                  <Route path="/architect" element={isAdmin ? <ArchitectEnrollmentView setShowAuthModal={handleAuthTrigger} /> : <Navigate to="/" replace />} />
                  <Route path="/partners" element={isAdmin ? <PartnerEnrollmentView setShowAuthModal={handleAuthTrigger} /> : <Navigate to="/" replace />} />
                  <Route path="/prices/:productSlug/:citySlug" element={<ErrorBoundary><DiscoveryView setShowAuthModal={handleAuthTrigger} /></ErrorBoundary>} />
                  <Route path="/stores/cheapest-in/:citySlug" element={<ErrorBoundary><DiscoveryView setShowAuthModal={handleAuthTrigger} /></ErrorBoundary>} />
                  <Route path="/history/:productSlug" element={<ErrorBoundary><DiscoveryView setShowAuthModal={handleAuthTrigger} /></ErrorBoundary>} />
                  <Route path="/battle/:stores/:citySlug" element={<ErrorBoundary><DiscoveryView setShowAuthModal={handleAuthTrigger} /></ErrorBoundary>} />
                  <Route path="/blog" element={<ErrorBoundary><BlogView setShowAuthModal={handleAuthTrigger} /></ErrorBoundary>} />
                  <Route path="/blog/:slug" element={<ErrorBoundary><BlogPostView setShowAuthModal={handleAuthTrigger} /></ErrorBoundary>} />
                  <Route path="/alternatives" element={<ErrorBoundary><AlternativesView setShowAuthModal={handleAuthTrigger} /></ErrorBoundary>} />
                  <Route path="/alternatives/:slug" element={<ErrorBoundary><AlternativeView setShowAuthModal={handleAuthTrigger} /></ErrorBoundary>} />
                  <Route path="/newsroom" element={<ErrorBoundary><NewsroomView setShowAuthModal={handleAuthTrigger} /></ErrorBoundary>} />
                  <Route path="/newsroom/:slug" element={<ErrorBoundary><NewsroomPostView setShowAuthModal={handleAuthTrigger} /></ErrorBoundary>} />
                  <Route path="/sign-in/*" element={<ErrorBoundary><SignInView /></ErrorBoundary>} />
                  <Route path="/sign-up/*" element={<ErrorBoundary><SignUpView /></ErrorBoundary>} />
                </Routes>
              </Suspense>
            </div>
          ) : (isLoaded && isSignedIn && userProfile) ? (
            <Suspense fallback={<CinematicLoader />}>
            <div className="flex-1 flex flex-col min-h-0 w-full">
              <SEO page="app" subTitle={getAppSubTitle()} />
              <div className="flex-1 flex flex-col min-h-0">
                <CartAssistantProvider
                  components={components}
                  tools={assistantTools}
                  systemPrompt={systemPrompt}
                  userKey={stableUserKey && stableUserKey.startsWith('user_') ? stableUserKey : undefined}
                  contextHelpers={assistantContextHelpers}
                  supabase={supabaseClient}
                  language={i18n.language}
                >
                  <CartAssistantBridge>
                    <Routes>
                      <Route path="/" element={<Navigate to={getDashboardPath()} replace />} />
                      <Route path="/home/*" element={
                        <PrivateRoute>
                          <div className="flex-1 flex flex-col min-h-0 w-full relative">
                            <NavigationSidebar isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} pathname={location.pathname} search={location.search} isProUser={isProUser} isUltraUser={isUltraUser} userRole={userProfile?.role} isArchitect={userProfile?.is_architect} isPartner={userProfile?.is_partner} onNavigate={p => { navigate(p); setIsMenuOpen(false); }} onLanguageChange={handleLanguageChange} />
                            <div className="flex-1 flex flex-col w-full min-h-0">
                              <div className={cn("mx-auto w-full flex flex-col flex-1 relative min-h-0", location.pathname === '/home/admin' || location.pathname.startsWith('/home/admin/') ? 'max-w-[1440px]' : 'max-w-lg')}>
                                <AppHeader onMenuOpen={() => setIsMenuOpen(true)} showStoreModal={() => setShowStoreModal(true)} currentStore={currentStore} isDetectingStore={isDetectingStore} currencySymbol={currencySymbol} totalBasket={totalBasket} showBasketInsights={() => setIsInsightModalOpen(true)} hasItems={activeItems.length > 0} isHome={location.pathname === '/home'} hasChanges={tipsProfileChanged || lastBasketChangeTimestamp > lastInsightGeneratedTimestamp} totalToneClass={budgetState ? BUDGET_TONE[budgetState.status].text : null} onClearBasket={() => setShowClearConfirm(true)} isUltra={isUltraUser} isPro={isProUser} credits={userProfile?.credits} onChatOpen={() => setIsChatOpen(true)} onBulkImportOpen={() => (isProUser || isUltraUser) ? setIsBulkImportOpen(true) : setShowUpgradeModal({ show: true, feature: 'bulk_import' })} bulkImportIndicator={bulkImportIndicatorColor} bulkImportPendingCount={bulkImportStatus.pendingCount} isUsageLimitReached={isUsageLimitReached} isSyncingSubscription={isSyncingSubscription} isSyncing={isSyncingToCloud || isSyncing} isPersonnelMode={isLatticeMode} />
                                {location.pathname === '/home' && !isLatticeMode && activeItems.length === 0 && processingCount === 0 && !isCameraOpen && (
                                  <BlogFirstScanGuide key={user.id} userId={user.id} createdAt={user.createdAt}
                                    onScan={() => { setScanMode('receipt'); setIsCameraOpen(true); }}
                                    onUpload={() => { setScanMode('import'); fileInputRef.current?.click(); }} />
                                )}
                                <AnimatePresence>
                                  {!isBulkImportOpen && bulkImportStatus.pendingCount > 0 && (
                                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="shrink-0 overflow-hidden px-4 pt-3">
                                      <button onClick={() => setIsBulkImportOpen(true)} className="flex w-full items-center gap-3 rounded-[2rem] border border-emerald-400/20 bg-[#111c2d] p-4 text-left text-emerald-300">
                                        <Loader2 size={18} className="animate-spin shrink-0" aria-hidden="true" />
                                        <span role="status" className="text-xs font-bold">{t('bulk_import.background_progress', { count: bulkImportStatus.pendingCount, defaultValue: 'Bulk import in progress: {{count}}' })}</span>
                                      </button>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                                <AnimatePresence>{isSyncing && (
                                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="fixed bottom-24 right-6 z-[100] flex flex-col items-center gap-3 p-4 bg-[#1a2b3c] rounded-2xl shadow-2xl border border-white/10 pointer-events-none">
                                    <div className="flex items-center gap-3"><Loader2 className="w-5 h-5 text-emerald-500 animate-spin" /><div className="flex flex-col"><span className="text-[10px] font-black text-white uppercase tracking-widest">Synchronizing Stack</span><span className="text-[8px] font-bold text-gray-400 uppercase tracking-tighter">Calibrating Protocol...</span></div></div>
                                  </motion.div>
                                )}</AnimatePresence>
                                <AnimatePresence>{processingCount > 0 && (
                                  <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="px-4 pt-4 shrink-0">
                                    <div className="bg-[#1a2b3c] text-white rounded-[2rem] p-6 flex items-center justify-between gap-4 shadow-xl animate-pulse relative overflow-hidden">
                                      <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent" />
                                      <div className="flex items-center gap-4 relative z-10"><div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center"><Loader2 className="animate-spin text-emerald-400" size={24} /></div><div><span className="font-black text-sm block tracking-tight">{t('app.analyzing_items')}</span><span className="text-[9px] text-gray-400 uppercase font-black tracking-widest">{t('app.reading_data')}</span></div></div>
                                      <div className="bg-white/5 px-3 py-2 rounded-xl border border-white/5 flex flex-col items-end relative z-10"><span className="text-[8px] font-black uppercase text-gray-500 tracking-widest">{t('app.recording_at')}</span><div className="flex items-center gap-1.5 text-emerald-400"><Clock size={12} /><span className="text-[10px] font-black">{new Date().toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}</span></div></div>
                                    </div>
                                  </motion.div>
                                )}</AnimatePresence>
                                <SovereignTourController />
                                <main className={cn("flex-1 overflow-y-auto", location.pathname.replace(/\/+$/, '') === '/home/admin' ? 'p-0' : 'px-4 pt-6 pb-4')}>
                                  <Suspense fallback={<div className="flex flex-col items-center justify-center py-40 h-full"><Loader2 className="w-10 h-10 text-emerald-500 animate-spin mb-4" /><p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] animate-pulse">{t('app.syncing')}</p></div>}>
                                    <Routes>
                                      <Route index element={<ShoppingList items={activeItems} onRemove={handleRemoveItem} onUpdate={updateItem} categories={categories} currencySymbol={currencySymbol} onImageUpload={handleItemImageUpload} onPreviewImage={setPreviewImageUrl} minimized={isPanelMinimized} suggestions={basketSuggestions} storePriceChanges={storePriceChanges} hasRestockItems={restockItems.length > 0} currentStoreName={currentStore?.name} onAddSuggested={handleAddSuggested} onSnoozeSuggested={handleSnoozeRestock} onHideSuggested={handleHideRestock} isUsageLimitReached={isUsageLimitReached} archiveNextSlotAt={archiveAllowance?.nextSlotAt ?? null} archiveLimit={archiveAllowance?.limit ?? 5} archiveWindowDays={archiveAllowance?.windowDays ?? 30} isPersonnelMode={isLatticeMode} latticeSeedItems={latticeSeedItems} isLoadingLatticeSeed={isLoadingLatticeSeed} latticeSeedScope={latticeSeedScope} showLatticeSeed={shouldShowLatticeSeed} onSeedItemTap={handleSeedItemTap} />} />
                                      <Route path="dashboard" element={<ErrorBoundary><DashboardView history={sortedHistory} categories={categories} analytics={analytics} persistedInsight={masterInsight} onSaveInsight={handleSaveInsight} restockItems={restockItems} onAddSuggested={handleAddSuggested} onSnoozeRestock={handleSnoozeRestock} onHideRestock={handleHideRestock} lastHistoryUpdate={lastHistoryUpdate} lastSettingsUpdate={lastSettingsUpdate} lastBasketChangeTimestamp={lastBasketChangeTimestamp} isProUser={isProUser} isUltraUser={isUltraUser} isInsightOnCooldown={isInsightOnCooldown} cooldownTimeLeft={String(cooldownTimeLeft)} aiProfile={userProfile?.ai_profile} currencySymbol={currencySymbol} items={activeItems} onBypassCooldown={() => handleBypassCooldownAsync('audit')} /></ErrorBoundary>} />
                                      <Route path="scout" element={<ErrorBoundary><FeatureGatedRoute isAllowed={isAdmin} isLoading={!userProfile} tier="ultra" featureName="Scout Terminal"><ScoutDashboard supabaseClient={supabaseClient!} /></FeatureGatedRoute></ErrorBoundary>} />
                                      <Route path="merchant" element={<ErrorBoundary><FeatureGatedRoute isAllowed={isAdmin} isLoading={!userProfile} tier="ultra" featureName="Merchant Pulse"><MerchantDashboard supabaseClient={supabaseClient!} /></FeatureGatedRoute></ErrorBoundary>} />
                                      <Route path="history" element={<ErrorBoundary><HistoryView history={sortedHistory} supabaseClient={supabaseClient} userId={user?.id} onClose={handleCloseHistory} onDeleteEntry={handleDeleteHistoryEntry} onClearHistory={handleClearHistory} onPreviewImage={handlePreviewImage} onBoundsChange={handleHistoryBoundsChange} currencySymbol={currencySymbol} onLoadMore={loadMoreHistory} isFetching={isFetchingHistory} hasMore={hasMoreHistory} isProUser={isProUser} isUltraUser={isUltraUser} freeHistoryDays={archiveAllowance?.windowDays ?? 30} onUpgrade={f => setShowUpgradeModal({ show: true, feature: f })} /></ErrorBoundary>} />
                                      <Route path="categories" element={<CategoryManager categories={categories} onAdd={(n, c, e) => setCategories([...categories, { name: n, color: c, emoji: e }])} onRemove={n => setCategories(categories.filter(cat => cat.name !== n))} onUpdate={(o, n, c, e) => setCategories(categories.map(cat => cat.name === o ? { name: n, color: c, emoji: e } : cat))} onClose={() => navigate('/home')} />} />
                                      <Route path="settings" element={<ErrorBoundary><SettingsView onClose={() => navigate('/home')} userProfile={userProfile} supabaseClient={supabaseClient!} showNotification={showNotification} onRefreshPlan={async () => { await Promise.all([refreshAccount(supabaseClient!, user!.id), reconcileEntitlement(getToken, user!.id)]); }} isUltraUser={isUltraUser} onLanguageChange={handleLanguageChange} onCurrencyChange={handleCurrencyChange} /></ErrorBoundary>} />
                                      <Route path="account" element={<ErrorBoundary><AccountView onClose={() => navigate('/home')} supabaseClient={supabaseClient!} userProfile={userProfile} getToken={getToken} showNotification={showNotification} /></ErrorBoundary>} />
                                      <Route path="admin" element={<FeatureGatedRoute isAllowed={isAdmin} isLoading={!userProfile} tier="ultra" featureName={t('admin_navigation.title', { defaultValue: 'Admin' })}><Outlet /></FeatureGatedRoute>}>
                                        <Route index element={<AdminTerminal supabaseClient={supabaseClient!} onNavigate={navigate} showNotification={showNotification} />} />
                                        <Route path="blog" element={<BlogCMS key="blog" showNotification={showNotification} initialMode="post" supabaseClient={supabaseClient!} />} />
                                        <Route path="alternatives" element={<Navigate to={alternativesStudioTarget} replace />} />
                                        <Route path="metrics" element={<ErrorBoundary><AdminMetrics /></ErrorBoundary>} />
                                        <Route path="upgrade-role" element={<EnrollmentCenter userProfile={userProfile} supabaseClient={supabaseClient!} onNavigate={navigate} showNotification={showNotification} />} />
                                        <Route path="newsroom" element={<BlogCMS key="newsroom" showNotification={showNotification} initialMode="newsroom" supabaseClient={supabaseClient!} />} />
                                      </Route>
                                      <Route path="apply" element={isAdmin ? <Navigate to={`/home/admin/upgrade-role${location.search}${location.hash}`} replace /> : <EnrollmentCenter userProfile={userProfile} supabaseClient={supabaseClient!} onNavigate={navigate} showNotification={showNotification} />} />
                                      <Route path="blog" element={<Navigate to={`/home/admin/blog${location.search}${location.hash}`} replace />} />
                                      <Route path="alternatives" element={<Navigate to={alternativesStudioTarget} replace />} />
                                      <Route path="newsroom" element={<Navigate to={`/home/admin/newsroom${location.search}${location.hash}`} replace />} />

                                      <Route path="comparisons" element={<ErrorBoundary><FeatureGatedRoute isAllowed={isProUser || isUltraUser} isLoading={!userProfile} tier="pro" featureName={t('menu.price_trends')}><ComparisonView history={sortedHistory} itemData={analytics?.item_data} currentItems={activeItems} currentStore={currentStore} onClose={() => navigate('/home')} lastHistoryUpdate={lastHistoryUpdate} isProUser={isProUser} isUltraUser={isUltraUser} currencySymbol={currencySymbol} supabaseClient={supabaseClient!} userId={user!.id} /></FeatureGatedRoute></ErrorBoundary>} />
                                      <Route path="*" element={<Navigate to="/home" replace />} />
                                    </Routes>
                                  </Suspense>
                                </main>
                                {location.pathname === '/home' && !isLatticeMode && <AppBottomPanel isPanelMinimized={isPanelMinimized} setIsPanelMinimized={setIsPanelMinimized} items={activeItems} totalBasket={totalBasket} receiptSubtotal={receiptSubtotal} currencySymbol={currencySymbol} saveToHistory={() => setShowArchiveModal(true)} setScanMode={setScanMode} setIsCameraOpen={setIsCameraOpen} fileInputRef={fileInputRef} setShowAddModal={setShowAddModal} handleShowBasketInsights={() => setIsInsightModalOpen(true)} isStoreLocked={!currentStore} processingCount={processingCount} currentStore={currentStore} tripDateTime={tripDateTime} setShowDatePicker={setShowDatePicker} isTimeManual={isTimeManual} setIsTimeManual={(v) => { if (!v) setTripDateTime(new Date()); setIsTimeManual(v); }} isUsageLimitReached={isUsageLimitReached} freeArchivesLeft={freeArchivesLeft} onUpgradeClick={() => setShowUpgradeModal({ show: true, feature: t('app.limited_feature_basket') })} isUltra={isUltraUser} isPro={isProUser} isAdmin={userProfile?.role === 'admin'} tripBudget={tripBudget} withBudget={withBudget} onEditBudget={() => setIsBudgetSheetOpen(true)} />}
                                {isLatticeMode && <AppBottomPanel isPanelMinimized={isPanelMinimized} setIsPanelMinimized={setIsPanelMinimized} items={activeItems} totalBasket={totalBasket} receiptSubtotal={receiptSubtotal} currencySymbol={currencySymbol} saveToHistory={() => setShowArchiveModal(true)} setScanMode={setScanMode} setIsCameraOpen={setIsCameraOpen} fileInputRef={fileInputRef} setShowAddModal={setShowAddModal} handleShowBasketInsights={() => {}} isStoreLocked={!currentStore} processingCount={processingCount} currentStore={currentStore} tripDateTime={tripDateTime} setShowDatePicker={() => {}} isTimeManual={false} setIsTimeManual={() => {}} isUsageLimitReached={false} onUpgradeClick={() => {}} isUltra={true} isPro={false} isPersonnelMode={true} isAdmin={userProfile?.role === 'admin'} />}
                                {isUltraUser && <MessageThreadCollapsible onPreviewImage={setPreviewImageUrl} supabaseClient={supabaseClient} userId={user?.id} />}
                              </div>
                            </div>
                          </div>
                        </PrivateRoute>
                      } />
                      <Route path="*" element={<Navigate to="/home" replace />} />
                    </Routes>
                  </CartAssistantBridge>
                  <StorePickerModal isOpen={showStoreModal} onClose={() => setShowStoreModal(false)} storePickerMode={storePickerMode} setStorePickerMode={setStorePickerMode} manualStoreSearch={manualStoreSearch} setManualStoreSearch={setManualStoreSearch} handleStoreSearch={handleStoreSearch} isSearchingStores={isSearchingStores} handleDetectStore={handleDetectStore} isDetectingStore={isDetectingStore} nearbyStores={nearbyStores} selectStore={selectStore} mapContainerRef={mapContainerRef} manualStoreName={manualStoreName} setManualStoreName={setManualStoreName} manualStoreAddress={manualStoreAddress} setManualStoreAddress={setManualStoreAddress} handleManualStoreSubmit={handleManualStoreSubmit} isGeocodingStore={isGeocodingStore} addressSuggestions={addressSuggestions} setAddressSuggestions={setAddressSuggestions} isSearchingAddress={isSearchingAddress} onAddressSelect={onAddressSelect} userCoords={userCoords} currentStore={currentStore} />
                  <TripBudgetModal isOpen={isBudgetSheetOpen} storeName={currentStore?.name} currencySymbol={currencySymbol} initialAmount={tripBudget} onSave={finishBudgetSheet} onClose={closeBudgetSheet} onSkip={() => finishBudgetSheet('skip')} />
                  <AddProductModal isOpen={showAddModal} onClose={handleCloseAddModal} brand={brand} setBrand={setBrand} product={product} setProduct={setProduct} variety={variety} setVariety={setVariety} size={size} setSize={setSize} unit={unit} setUnit={setUnit} price={newItemPrice} setPrice={setNewItemPrice} category={newItemCategory} setCategory={setNewItemCategory} image={newItemImage} setImage={setNewItemImage} metadata={newItemMetadata} quantity={newItemQuantity} setQuantity={setNewItemQuantity} canonicalId={newItemCanonicalId} setCanonicalId={setNewItemCanonicalId} embedding={newItemEmbedding} setEmbedding={setNewItemEmbedding} shelfLife={newItemShelfLife} setShelfLife={setNewItemShelfLife} expirationDate={newItemExpirationDate} setExpirationDate={setNewItemExpirationDate} upc={newItemUPC} setUpc={setNewItemUPC} gtin={newItemGTIN} setGtin={setNewItemGTIN} isExtracting={isExtractingManual} onImageProcessed={handleManualImageExtraction} categories={categories} currencySymbol={currencySymbol} tripDateTime={tripDateTime} onAdd={handleAddToBasket} />
                  {showDatePicker && <Suspense fallback={null}><DateTimePickerModal currentValue={tripDateTime} onSave={(d) => { setTripDateTime(d); setReceiptDatePending(false); setIsTimeManual(true); closeDatePicker(); }} onClose={closeDatePicker} /></Suspense>}
                  {/* On-demand lazy views get their own boundary: suspending the app-wide one on first open
                      cuts off open sheets' exit animations (the Trip Budget sheet stayed stuck over the camera). */}
                  {isCameraOpen && <Suspense fallback={null}><CameraView mode={scanMode === 'receipt' ? 'receipt' : 'tag'} onCapture={(base64, blob) => handleCapture(base64, blob)} onClose={() => setIsCameraOpen(false)} /></Suspense>}
                  {isBulkImportOpen && supabaseClient && user?.id && (
                    <Suspense fallback={null}>
                    <BulkImportView
                      onClose={() => setIsBulkImportOpen(false)}
                      supabaseClient={supabaseClient}
                      userId={user.id}
                      categories={categories}
                      currencySymbol={currencySymbol}
                      isProUser={isProUser}
                      isUltraUser={isUltraUser}
                      latticeParticipationEnabled={latticeParticipation}
                      onUpgrade={(feature) => setShowUpgradeModal({ show: true, feature })}
                      onArchiveComplete={() => load(supabaseClient, user.id, false)}
                    />
                    </Suspense>
                  )}
                  <BasketInsightsModal isOpen={isInsightModalOpen} onClose={() => setIsInsightModalOpen(false)} isGenerating={isGeneratingTips} basketInsight={tipsProfileChanged ? null : basketInsight} onUpdate={handleUpdateBasketInsights} hasChanges={tipsProfileChanged || lastBasketChangeTimestamp > lastInsightGeneratedTimestamp} isEmpty={activeItems.length === 0} isProUser={isProUser} isUltraUser={isUltraUser} isInsightOnCooldown={isInsightOnCooldown} cooldownTimeLeft={String(cooldownTimeLeft)} onBypassCooldown={() => handleBypassCooldownAsync('tips')} />
                  {globalReceiptEntry && <Suspense fallback={null}><DigitalReceiptModal isOpen={isGlobalReceiptOpen} onClose={() => setIsGlobalReceiptOpen(false)} entry={globalReceiptEntry} currencySymbol={currencySymbol} /></Suspense>}
                  {previewImageUrl && <Suspense fallback={null}><ImagePreviewModal imageUrl={previewImageUrl} receiptZoom={previewReceiptZoom} onClose={() => setPreviewImageUrl(null)} /></Suspense>}
                  <LatticeSeedDetailModal item={previewSeedItem} isOpen={!!previewSeedItem} onClose={() => setPreviewSeedItem(null)} onViewPrices={handleViewSeedItemPrices} currencySymbol={currencySymbol} supabaseClient={supabaseClient} scope={latticeSeedScope} />
                  <ConfirmationModal isOpen={showClearConfirm} onClose={() => setShowClearConfirm(false)} onConfirm={async () => { clearBasket(); setReceiptDatePending(false); setShowDatePicker(false); const { ImageCacheService } = await import('./services/imageCacheService'); await ImageCacheService.clearAll(); }} title={t('basket.clear_title')} message={t('basket.clear_message')} confirmLabel={t('basket.yes_clear')} cancelLabel={t('basket.keep')} variant="danger" />
                  <ArchiveTripModal isOpen={showArchiveModal} currencySymbol={currencySymbol} isLatticeMode={isLatticeMode} isSaving={isHardSyncing} onConfirm={() => { void saveToHistory(isLatticeMode || undefined); }} onCancel={() => setShowArchiveModal(false)} />
                  {showUpgradeModal.show && (
                    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowUpgradeModal({ show: false, feature: '' })}
                        className="absolute inset-0 bg-[#1a2b3c]/60 backdrop-blur-md"
                      />
                      <motion.div 
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-sm relative z-10"
                      >
                        <button 
                          onClick={() => setShowUpgradeModal({ show: false, feature: '' })} 
                          className="absolute -top-12 right-0 w-12 h-12 flex items-center justify-center text-white/60 hover:text-white transition-all z-50 rounded-full hover:bg-white/5"
                        >
                          <X size={24} />
                        </button>
                        <UpgradeToProMessage featureName={showUpgradeModal.feature} isLimitReached={true} targetTier={showUpgradeModal.tier || (isProUser ? 'Ultra' : 'Pro')} />
                      </motion.div>
                    </div>
                  )}
                </CartAssistantProvider>
              </div>
            </div>
            </Suspense>
          ) : sessionLost ? (
            <SessionLostNotice />
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center">
              <LandingPage setShowAuthModal={handleAuthTrigger} />
            </div>
          )}
          <AnimatePresence>{showAuthModal && <AuthModal showAuthModal={showAuthModal} setShowAuthModal={handleAuthTrigger} />}</AnimatePresence>
        </div>
      </SupabaseContext.Provider>
      <PWAUpdateModal show={needRefresh || DEBUG_PWA_UPDATE} onUpdate={handleUpdateProtocol} onClose={() => setNeedRefresh(false)} />
      <PWAInstallModal show={showInstallModal && !isStandalone} onInstall={handleInstallPWA} onClose={handleDismissInstall} />
      <LanguageDetectionModal
        show={showLanguageModal}
        detectedCountryCode={detectedCountryCode}
        detectedCountryName={detectedCountryName}
        recommendedLang={recommendedLangCode}
        onSwitch={handleLanguageSwitch}
        onKeep={handleLanguageKeep}
        onClose={() => setShowLanguageModal(false)}
      />

      <MaintenanceModal isOpen={showMaintenanceModal} onClose={() => setShowMaintenanceModal(false)} maintenance={maintenance} />
    </GlobalErrorBoundary>
  );
};

export default App;
